import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { lexiconAgentService } from '../services/api';
import { getUser } from '../services/auth';
import { showBrandedAlert, showBrandedConfirm } from './BrandedDialog';
import {
  Play, PlayCircle, RotateCcw, Trash2, History, FileText, CheckSquare,
  Coins, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Download, Trash, BookOpen, Ship,
  ArrowLeft, Share2, Edit3, X, CalendarDays, Globe2, Languages, Check, Search, Hexagon, Filter, ListPlus
} from 'lucide-react';
import './LexiconWorkspace.css';

const STORE_KEY = 'agentic-article-lexicon-workspace';
const LIBRARY_PAGE_SIZE_OPTIONS = [10, 25, 50];
const VARIANT_PAGE_SIZE = 8;
const FETCH_HISTORY_LIMIT = 100;
const BULK_SOURCE_EXTRACT_CHARS = 1200;
const VARIANT_DISCOVERY_CHUNK_SIZE = 10;
const VARIANT_DISCOVERY_CONCURRENCY = 3;
const LIBRARY_CHECK_CHUNK_SIZE = 1000;
const RANDOM_LEXICON_TOPICS = [
  'Artificial intelligence',
  'Climate change',
  'Blockchain',
  'Mars',
  'Photosynthesis',
  'Black hole',
  'Quantum computing',
  'Democracy',
  'Inflation',
  'Machine learning',
  'Biodiversity',
  'Renewable energy',
  'Electric vehicle',
  'World War II',
  'Internet',
  'Cricket',
  'Yoga',
  'Himalayas',
  'Titan',
  'Apple',
  'Aamir Khan',
  'Virat Kohli',
  'Mahatma Gandhi',
  'Taj Mahal',
  'Python programming',
  'Data science',
  'Cybersecurity',
  'Solar system',
  'Human brain',
  'Globalization'
];
const FALLBACK_STYLES = [
  { value: 'neutral', label: 'Neutral / factual', guidance: 'Write in neutral, factual, encyclopedic prose as a single paragraph.' },
  { value: 'casual', label: 'Casual', guidance: 'Write in a casual, conversational tone, as if explaining to a friend.' },
  { value: 'academic', label: 'Academic', guidance: 'Write in a formal, academic tone using precise terminology.' },
  { value: 'eli5', label: 'Explain simply (ELI5)', guidance: 'Explain in very simple terms, as if to a curious child.' },
  { value: 'bullets', label: 'Bullet points', guidance: 'Present the summary as concise bullet points of the key facts.' }
];
const FALLBACK_OPTIONS = {
  styles: FALLBACK_STYLES.map(({ value, label }) => ({ value, label })),
  default_style: 'neutral',
  default_length: 100
};

function parseTopicList(text) {
  return text
    .split(/\r?\n/)
    .map(line => (line.split(',')[0] || '').trim().replace(/^"|"$/g, '').trim())
    .filter(Boolean);
}

function withSelegicUtm(value) {
  if (!value) return value;
  try {
    const url = new URL(String(value));
    url.searchParams.set('utm_source', 'selegic_encye');
    return url.toString();
  } catch {
    return String(value).replace(/utm_source=(openai|open_ai)/gi, 'utm_source=selegic_encye');
  }
}

function slugify(title) {
  return String(title || '').trim().replace(/\s+/g, '_') || 'entry';
}

function normalizeQueueTitle(title) {
  return String(title || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function summaryDisplayParts(content, fallbackUrl) {
  const text = String(content || '');
  const markdownLink = text.match(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/);
  const plainUrl = text.match(/https?:\/\/[^\s)]+/);
  const sourceUrl = withSelegicUtm(markdownLink?.[2] || plainUrl?.[0] || fallbackUrl);
  const summary = text
    .replace(/\(\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)\)/g, '')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, '')
    .replace(/\(https?:\/\/[^\s)]+\)/g, '')
    .replace(/https?:\/\/[^\s)]+/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return { summary, sourceUrl };
}

async function saveLexiconArticle(payload) {
  try {
    return await lexiconAgentService.saveArticle(payload);
  } catch (error) {
    if (error.response?.status === 409 && error.response?.data?.code === 'LEXICON_EXISTS' && error.response?.data?.canOverride) {
      const shouldOverride = await showBrandedConfirm({
        title: 'Override existing lexicon?',
        message: 'This organization already has this lexicon. Override its description with the new one?',
        confirmLabel: 'Override',
      });
      if (shouldOverride) {
        return lexiconAgentService.saveArticle({ ...payload, overrideExisting: true });
      }
    }
    throw error;
  }
}

async function publishLexiconArticleToEncye(articleId, { canManageLexicons, overrideEncye = false }) {
  try {
    return await lexiconAgentService.publishArticleToEncye(articleId, overrideEncye ? { overrideEncye: true } : {});
  } catch (error) {
    throw error;
  }
}

function duplicateTitleMessage(matches, prefix) {
  const shown = matches.map(match => match.encyeTitle || match.title || 'Untitled lexicon').slice(0, 10);
  return `${prefix}: ${shown.join(', ')}${matches.length > shown.length ? ', ...' : ''}`;
}

function isMissingEncyeCheck(error) {
  const detail = [
    error.response?.data?.detail,
    error.response?.data?.error,
    error.response?.data?.msg
  ].filter(Boolean).join(' ').toLowerCase();
  return [401, 403, 404].includes(error.response?.status) || detail.includes('lexicon not found');
}

async function confirmEncyeDuplicatePublish(titles, { canManageLexicons }) {
  const cleanTitles = [...new Set(titles.map(title => String(title || '').trim()).filter(Boolean))];
  if (!cleanTitles.length) return { proceed: true, overrideEncye: false };

  const response = await lexiconAgentService.checkExistingEncyeLexicons({ titles: cleanTitles }).catch(error => {
    if (isMissingEncyeCheck(error)) return { data: { matches: [], canOverride: canManageLexicons } };
    throw error;
  });
  const matches = response.data?.matches || [];
  if (!matches.length) return { proceed: true, overrideEncye: false };

  if (!canManageLexicons || !response.data?.canOverride) {
    await showBrandedAlert({
      title: 'Duplicate lexicons found',
      message: `${duplicateTitleMessage(matches, 'These lexicons are already present in Encye')}. Remove them before publishing to Encye. You are not admin, so you cannot override any present lexicon.`,
      confirmLabel: 'OK',
      tone: 'warning'
    });
    return { proceed: false, overrideEncye: false };
  }

  const shouldOverride = await showBrandedConfirm({
    title: 'Override in Encye?',
    message: `${duplicateTitleMessage(matches, 'These lexicons already exist in Encye')}. Override their Encye content with this generated content?`,
    confirmLabel: 'Override',
    tone: 'warning'
  });
  return { proceed: shouldOverride, overrideEncye: shouldOverride };
}

async function confirmLibraryDuplicateAction(items, { canManageLexicons, getTitle, removeItems }) {
  const duplicates = items.filter(item => item.presentInLibrary && !item.presentInEncye && !item.articleId);
  if (!duplicates.length) return { proceed: true, overrideExisting: false };
  const shown = duplicates.map(getTitle).slice(0, 10);
  if (!canManageLexicons) {
    const remove = await showBrandedConfirm({
      title: 'Existing lexicons in library',
      message: `These lexicons already exist in the library: ${shown.join(', ')}${duplicates.length > shown.length ? ', ...' : ''}. Remove them from here and publish later from Library.`,
      confirmLabel: 'Remove topic',
      tone: 'warning'
    });
    if (remove) removeItems(duplicates);
    return { proceed: false, overrideExisting: false };
  }
  const override = await showBrandedConfirm({
    title: 'Override library lexicons?',
    message: `These lexicons already exist in the library: ${shown.join(', ')}${duplicates.length > shown.length ? ', ...' : ''}. Override their library content with this generated content?`,
    confirmLabel: 'Override',
    tone: 'warning'
  });
  return { proceed: override, overrideExisting: override };
}

async function showLexiconLimitModal(error) {
  const data = error.response?.data || {};
  if (data.code !== 'LEXICON_DAILY_LIMIT') return false;
  const limit = Number(data.limit || 100);
  const usedToday = Number(data.usedToday || 0);
  const remaining = Number(data.remaining || 0);
  await showBrandedAlert({
    title: 'Daily lexicon limit reached',
    message: `${data.detail || `You can generate up to ${limit} lexicons per day.`} Used today: ${usedToday}. Remaining today: ${remaining}. Admin users do not have this limit.`,
    confirmLabel: 'OK',
    tone: 'warning'
  });
  return true;
}

async function showExistingGenerationBlockedModal(error) {
  const data = error.response?.data || {};
  if (data.code !== 'LEXICON_EXISTING_GENERATION_FORBIDDEN') return false;
  const titles = Array.isArray(data.matches) ? data.matches.map(match => match.title).filter(Boolean).slice(0, 8) : [];
  await showBrandedAlert({
    title: 'Existing lexicons cannot be regenerated',
    message: `${data.detail || 'Some selected topics already exist in the library.'}${titles.length ? ` Existing: ${titles.join(', ')}${data.matches.length > titles.length ? ', ...' : ''}.` : ''} Remove existing topics from the queue to continue. Admin users can generate and override existing lexicons.`,
    confirmLabel: 'OK',
    tone: 'warning'
  });
  return true;
}

function clipBulkSourceExtract(value) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (text.length <= BULK_SOURCE_EXTRACT_CHARS) return text;
  return `${text.slice(0, BULK_SOURCE_EXTRACT_CHARS).trim()}...`;
}

function readStoredState() {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || '{}');
  } catch {
    return {};
  }
}

function exportToCsv(data, filename) {
  if (!data || !data.length) return;
  const header = ['Topic', 'Status', 'Summary', 'Words'];
  const rows = [header];
  for (const item of data) {
    const topic = `"${(item.topic || '').replace(/"/g, '""')}"`;
    const status = `"${(item.status || '').replace(/"/g, '""')}"`;
    const summary = `"${(item.summary || '').replace(/"/g, '""')}"`;
    const words = item.summary ? item.summary.trim().split(/\s+/).length : 0;
    rows.push([topic, status, summary, words]);
  }
  const csvContent = rows.map(e => e.join(",")).join("\n");
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function TokenUsageStat({ usage, itemCount }) {
  if (!usage?.total_tokens) return null;
  const input = Number(usage.input_tokens || 0);
  const output = Number(usage.output_tokens || 0);
  const total = Number(usage.total_tokens || 0);
  const count = Math.max(1, Number(itemCount || 0));
  const average = Math.round(total / count);
  return (
    <span
      className="lexicon-stat-item lexicon-token-stat"
      title={`Total OpenAI usage for this run. Input/context: ${input.toLocaleString()}. Output: ${output.toLocaleString()}. Average per generated entry: ${average.toLocaleString()}. Web search can add large context tokens.`}
    >
      <Coins size={14} /> {total.toLocaleString()} total
      <small>{input.toLocaleString()} input</small>
      <small>{output.toLocaleString()} output</small>
    </span>
  );
}

function stripMarkdownSyntax(text = '') {
  return text
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    .replace(/\[(.*?)\]\((.*?)\)/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\s*[-*]\s+/gm, '')
    .trim();
}

function renderInlineSummary(text) {
  const tokenRegex = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;
  return text.split(tokenRegex).map((part, index) => {
    const boldMatch = part.match(/^\*\*([^*]+)\*\*$/);
    if (boldMatch) {
      return <strong key={index}>{boldMatch[1]}</strong>;
    }
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      return (
        <a
          key={index}
          href={linkMatch[2]}
          target="_blank"
          rel="noreferrer"
          className="lexicon-source-link"
        >
          {linkMatch[1]}
        </a>
      );
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
}

function SummaryPreview({ text }) {
  const lines = text
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean);

  return (
    <div className="lexicon-summary-preview">
      {lines.map((line, index) => {
        const isBullet = /^\s*[-*]\s+/.test(line);
        const cleanLine = line
          .replace(/^#{1,6}\s+/, '')
          .replace(/^\s*[-*]\s+/, '');

        return (
          <p key={`${cleanLine}-${index}`} className={isBullet ? 'lexicon-summary-bullet' : undefined}>
            {renderInlineSummary(cleanLine)}
          </p>
        );
      })}
    </div>
  );
}

const defaultWorkspaceState = {
  style: '',
  length: 100,
  useCustomPrompt: false,
  customPrompt: '',
  batchStyle: '',
  batchLength: 100,
  batchUseCustomPrompt: false,
  batchCustomPrompt: '',
  fetchHistory: []
};

function OperationProgressModal({ operation }) {
  const [progress, setProgress] = useState(0);
  const [visibleOperation, setVisibleOperation] = useState(null);

  useEffect(() => {
    if (!operation?.active) {
      setProgress(prev => {
        if (prev <= 0) return 0;
        window.setTimeout(() => {
          setVisibleOperation(null);
          setProgress(0);
        }, 450);
        return 100;
      });
      return undefined;
    }
    setVisibleOperation(operation);
    const startedAt = Date.now();
    const duration = operation.duration || 18000;
    const indeterminate = Boolean(operation.indeterminate);
    setProgress(4);
    const id = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      if (indeterminate) {
        setProgress(prev => (prev >= 96 ? 12 : prev + 4));
        return;
      }
      const eased = 1 - Math.pow(1 - Math.min(elapsed / duration, 1), 2.2);
      setProgress(Math.min(99, Math.round(4 + eased * 95)));
    }, 220);
    return () => window.clearInterval(id);
  }, [operation]);

  const shownOperation = operation?.active ? operation : visibleOperation;
  if (!shownOperation) return null;

  return (
    <div className="lexicon-operation-backdrop" role="status" aria-live="polite">
      <div className="lexicon-operation-modal">
        <div className="lexicon-operation-logo">
          <Hexagon size={44} strokeWidth={1.7} />
        </div>
        <div>
          <h3>{shownOperation.title}</h3>
          <p>{shownOperation.detail}</p>
        </div>
        <div className={`lexicon-operation-progress${shownOperation.indeterminate ? ' is-indeterminate' : ''}`}>
          <div style={{ width: `${progress}%` }} />
        </div>
        <strong>{shownOperation.indeterminate ? (shownOperation.statusLabel || 'Working...') : `${progress}%`}</strong>
      </div>
    </div>
  );
}

const LexiconWorkspace = () => {
  const stored = useMemo(readStoredState, []);
  const currentUser = useMemo(getUser, []);
  const canManageLexicons = Boolean(currentUser?.permissions?.canManageResourcePrompts || currentUser?.permissions?.canManageBasePrompt);
  const [activeView, setActiveView] = useState('single');
  const [options, setOptions] = useState(FALLBACK_OPTIONS);
  const [styles, setStyles] = useState(FALLBACK_STYLES);
  const [toast, setToast] = useState('');
  const [state, setState] = useState({ ...defaultWorkspaceState, ...stored });
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [operation, setOperation] = useState(null);
  const toastTimerRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(STORE_KEY, JSON.stringify({
      style: state.style,
      length: state.length,
      useCustomPrompt: state.useCustomPrompt,
      customPrompt: state.customPrompt,
      batchStyle: state.batchStyle,
      batchLength: state.batchLength,
      batchUseCustomPrompt: state.batchUseCustomPrompt,
      batchCustomPrompt: state.batchCustomPrompt,
      fetchHistory: state.fetchHistory
    }));
  }, [state]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const notify = useCallback((message) => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
    }
    setToast(message);
    const duration = Math.min(12000, Math.max(3200, String(message || '').length * 85));
    toastTimerRef.current = window.setTimeout(() => {
      setToast('');
      toastTimerRef.current = null;
    }, duration);
  }, []);

  const loadOptions = useCallback(async () => {
    try {
      const response = await lexiconAgentService.getOptions();
      setOptions(response.data);
    } catch (error) {
      setOptions(FALLBACK_OPTIONS);
      notify(error.response?.data?.detail || error.response?.data?.error || 'Lexicon options are using defaults because the backend is unavailable.');
    }
  }, [notify]);

  const loadStyles = useCallback(async () => {
    try {
      const response = await lexiconAgentService.getStyles();
      setStyles(response.data);
    } catch (error) {
      setStyles(FALLBACK_STYLES);
    }
  }, []);

  useEffect(() => {
    loadOptions();
    loadStyles();
  }, [loadOptions, loadStyles]);

  useEffect(() => {
    if (!options.default_style) return;
    setState(prev => ({
      ...prev,
      style: prev.style || options.default_style,
      length: prev.length || options.default_length,
      batchStyle: prev.batchStyle || options.default_style,
      batchLength: prev.batchLength || options.default_length
    }));
  }, [options]);

  const updateWorkspace = (patch) => setState(prev => ({ ...prev, ...patch }));

  const refreshAgent = useCallback(async () => {
    await Promise.all([loadOptions(), loadStyles()]);
  }, [loadOptions, loadStyles]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const startOperation = useCallback((nextOperation) => {
    setOperation({ active: true, ...nextOperation });
  }, []);

  const finishOperation = useCallback(() => {
    setOperation(prev => prev ? { ...prev, active: false } : prev);
  }, []);

  return (
    <div className="lexicon-workspace">
      {toast && <div className="lexicon-toast">{toast}</div>}
      <OperationProgressModal operation={operation} />

      <div className="lexicon-workspace-header">
        <div>
          <h2>Lexicon</h2>
          <p>Generate, batch-create, save, and manage lexicon summaries from topic names and prompts.</p>
        </div>
      </div>

      <div className="lexicon-tabs" role="tablist" aria-label="Lexicon workspace views">
        {[
          ['single', 'Summarizer'],
          ['batch', 'Mass Creation'],
          ['library', 'Library'],
          ...(canManageLexicons ? [['settings', 'Settings']] : [])
        ].map(([value, label]) => (
          <button
            key={value}
            className={`lexicon-tab ${activeView === value ? 'lexicon-tab-active' : ''}`}
            onClick={() => setActiveView(value)}
          >
            {label}
          </button>
        ))}
      </div>

      {activeView === 'single' && (
        <SingleSummarizer
          options={options}
          workspaceState={state}
          updateWorkspace={updateWorkspace}
          notify={notify}
          canManageLexicons={canManageLexicons}
          startOperation={startOperation}
          finishOperation={finishOperation}
        />
      )}
      {activeView === 'batch' && (
        <BatchSummarizer
          options={options}
          workspaceState={state}
          updateWorkspace={updateWorkspace}
          notify={notify}
          canManageLexicons={canManageLexicons}
          startOperation={startOperation}
          finishOperation={finishOperation}
        />
      )}
      {activeView === 'library' && (
        <Library
          notify={notify}
          canManageLexicons={canManageLexicons}
          startOperation={startOperation}
          finishOperation={finishOperation}
        />
      )}
      {activeView === 'settings' && (
        <Settings
          options={options}
          styles={styles}
          refreshAgent={refreshAgent}
          notify={notify}
        />
      )}

      {showScrollTop && (
        <button
          className="lexicon-back-to-top"
          onClick={scrollToTop}
          aria-label="Back to top"
          title="Back to top"
        >
          <ChevronUp size={20} />
        </button>
      )}
    </div>
  );
};

function SummaryOptions({ options, values, onChange, batch = false }) {
  return (
    <div className="lexicon-panel">
      <div className="lexicon-panel-header">
        <h3>Summary options</h3>
        <p>{batch ? 'Applied to every topic in the single batched request.' : 'Applied to every item processed by the queue.'}</p>
      </div>
      <div className="lexicon-option-grid">
        <label>
          Style
          <select
            value={values.style}
            onChange={(event) => onChange({ style: event.target.value })}
            disabled={values.useCustomPrompt}
          >
            {options.styles.map(style => (
              <option key={style.value} value={style.value}>{style.label}</option>
            ))}
          </select>
        </label>
        <label>
          Length (words)
          <input
            type="number"
            min="20"
            max="500"
            step="10"
            value={values.length}
            onChange={(event) => onChange({ length: Number(event.target.value) })}
          />
        </label>
      </div>
      <label className="lexicon-checkbox">
        <input
          type="checkbox"
          checked={values.useCustomPrompt}
          onChange={(event) => onChange({ useCustomPrompt: event.target.checked })}
        />
        Use a custom prompt instead of the style
      </label>
      <textarea
        value={values.customPrompt}
        onChange={(event) => onChange({ customPrompt: event.target.value })}
        disabled={!values.useCustomPrompt}
        rows="6"
        placeholder="e.g. Summarize as numbered key takeaways, focusing on dates and people."
      />
    </div>
  );
}

function SingleSummarizer({ options, workspaceState, updateWorkspace, notify, canManageLexicons, startOperation, finishOperation }) {
  const [topicText, setTopicText] = useState('');
  const [queue, setQueue] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [runUsage, setRunUsage] = useState(null);
  const [running, setRunning] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [fetchCount, setFetchCount] = useState(3);
  const [fetchingRandom, setFetchingRandom] = useState(false);
  const [searchingVariants, setSearchingVariants] = useState(false);
  const [generatingWithVariants, setGeneratingWithVariants] = useState(false);
  const [collapsedCards, setCollapsedCards] = useState(new Set());
  const [expandedVariantPickers, setExpandedVariantPickers] = useState(new Set());
  const [savingIds, setSavingIds] = useState(new Set());
  const [publishingIds, setPublishingIds] = useState(new Set());
  const [bulkPublishing, setBulkPublishing] = useState(false);
  const [activeJob, setActiveJob] = useState(null);

  const toggleCardCollapse = (id) => {
    setCollapsedCards(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleVariantPicker = (id) => {
    setExpandedVariantPickers(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const getCardIcon = (title = '') => {
    const t = title.toLowerCase();
    if (t.includes('journal') || t.includes('education') || t.includes('academic') || t.includes('science') || t.includes('history')) {
      return BookOpen;
    }
    if (t.includes('uss') || t.includes('ship') || t.includes('boat') || t.includes('victory') || t.includes('navy')) {
      return Ship;
    }
    return FileText;
  };

  const stoppedRef = useRef(false);
  const activeJobRef = useRef(null);
  const queueRef = useRef(queue);

  useEffect(() => {
    queueRef.current = queue;
  }, [queue]);

  const matchPercentage = (entry) => Math.round(Number(entry.confidence || 0) * 100);

  const addEntries = (entries, { sort = true } = {}) => {
    const sortedEntries = [...entries].sort((a, b) => {
      if (!sort) return 0;
      const aHasMatch = a.confidence !== undefined;
      const bHasMatch = b.confidence !== undefined;
      if (aHasMatch || bHasMatch) {
        return Number(bHasMatch) - Number(aHasMatch)
          || matchPercentage(b) - matchPercentage(a)
          || Number(b.confidence || 0) - Number(a.confidence || 0)
          || Number(b.popularityScore || 0) - Number(a.popularityScore || 0);
      }
      return 0;
    });
    const variantEntries = sortedEntries.filter(entry => entry.variantGroupKey);
    if (variantEntries.length) {
      console.log('[lexicon variants] Sorted variant cards by match percentage:', variantEntries.map(entry => ({
        title: entry.title,
        views: Number(entry.popularityScore || 0),
        group: entry.variantGroupKey,
        confidence: matchPercentage(entry),
        breakdown: entry.matchScoreBreakdown
      })));
    }
    const newItems = sortedEntries.map(entry => ({
      id: `${Date.now()}-${Math.random()}`,
      selected: entry.selected !== false,
      entry,
      isVariant: Boolean(entry.isVariant || entry.variantGroupKey),
      variantChecked: Boolean(entry.variantChecked || entry.variantGroupKey),
      variantGroupKey: entry.variantGroupKey,
      variantCandidates: entry.variantCandidates || [],
      status: 'pending'
    }));
    const nextQueue = [
      ...queueRef.current,
      ...newItems
    ];
    queueRef.current = nextQueue;
    setQueue(nextQueue);
    setRunUsage(null);
    setActiveJob(null);
    return newItems;
  };

  const applyLibraryPresence = useCallback(async (itemsOrIds) => {
    const targetIds = new Set((itemsOrIds || []).map(item => typeof item === 'string' ? item : item.id));
    const targetItems = queueRef.current.filter(item => !targetIds.size || targetIds.has(item.id));
    const titles = targetItems.map(item => item.entry.title);
    if (!titles.length) return;

    try {
      const matches = new Map();
      const encyeMatches = new Map();
      for (let start = 0; start < titles.length; start += LIBRARY_CHECK_CHUNK_SIZE) {
        const chunk = titles.slice(start, start + LIBRARY_CHECK_CHUNK_SIZE);
        const [response, encyeResponse] = await Promise.all([
          lexiconAgentService.checkExistingArticles({ titles: chunk }),
          lexiconAgentService.checkExistingEncyeLexicons({ titles: chunk }).catch(error => {
            if (isMissingEncyeCheck(error)) return { data: { matches: [] } };
            throw error;
          })
        ]);
        for (const match of response.data.matches || []) {
          matches.set(match.normalizedTitle, match);
        }
        for (const match of encyeResponse.data.matches || []) {
          encyeMatches.set(normalizeQueueTitle(match.title), match);
        }
      }
      const nextQueue = queueRef.current.map(item => {
        if (targetIds.size && !targetIds.has(item.id)) return item;
        const match = matches.get(normalizeQueueTitle(item.entry.title));
        const encyeMatch = encyeMatches.get(normalizeQueueTitle(item.entry.title));
        return {
          ...item,
          presentInLibrary: Boolean(match),
          libraryArticleId: match?.articleId || null,
          presentInEncye: Boolean(match?.encyeLexiconId || encyeMatch),
          encyeExistingLexiconId: match?.encyeLexiconId || encyeMatch?.encyeLexiconId || null
        };
      });
      queueRef.current = nextQueue;
      setQueue(nextQueue);
    } catch (error) {
      notify(error.response?.data?.detail || error.message);
    }
  }, [notify]);

  const candidateToEntry = (candidate, topic, groupKey) => ({
    title: candidate.title,
    extract: '',
    url: '',
    source: 'manual',
    descriptionHint: candidate.descriptionHint || '',
    selected: candidate.selected !== false,
    confidence: candidate.confidence,
    popularityScore: candidate.popularityScore,
    matchScoreBreakdown: candidate.matchScoreBreakdown,
    needsReview: candidate.needsReview,
    isVariant: true,
    variantChecked: true,
    variantGroupKey: groupKey,
    variantDetectionReason: `Discovered as a variant for "${topic}"`
  });

  const applyVariantResult = useCallback((item, result) => {
    const candidates = result?.candidates || [];
    const groupKey = result?.variantGroupKey;
    const variantCandidates = candidates
      .filter(candidate => normalizeQueueTitle(candidate.title) !== normalizeQueueTitle(item.entry.title))
      .map(candidate => candidateToEntry(candidate, item.entry.title, groupKey));
    const hasVariants = Boolean(result?.isVariantTopic) && variantCandidates.length > 0;
    const nextQueue = queueRef.current.map(row => (
      row.id === item.id
        ? {
            ...row,
            isVariant: hasVariants,
            variantChecked: true,
            variantGroupKey: hasVariants ? groupKey : row.variantGroupKey,
            variantCandidates: variantCandidates.map(candidate => ({ ...candidate, selectedForAdd: true })),
            variantSearchError: undefined,
            entry: {
              ...row.entry,
              isVariant: hasVariants,
              variantChecked: true,
              variantGroupKey: hasVariants ? groupKey : row.entry.variantGroupKey,
              variantDetectionReason: result?.reason || (hasVariants ? `Discovered variants for "${item.entry.title}"` : row.entry.variantDetectionReason)
            }
          }
        : row
    ));
    queueRef.current = nextQueue;
    setQueue(nextQueue);
  }, []);

  const applyVariantMetadataChunk = useCallback(async (items) => {
    try {
      const response = await lexiconAgentService.discoverVariantsBatch({
        topics: items.map(item => item.entry.title)
      });
      const byTopic = new Map((response.data.results || []).map(result => [normalizeQueueTitle(result.topic), result]));
      for (const item of items) {
        applyVariantResult(item, byTopic.get(normalizeQueueTitle(item.entry.title)));
      }
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Variant search failed.';
      const nextQueue = queueRef.current.map(row => (
        items.some(item => item.id === row.id)
          ? {
              ...row,
              isVariant: false,
              variantChecked: false,
              variantCandidates: [],
              variantSearchError: detail,
              entry: {
                ...row.entry,
                isVariant: false,
                variantChecked: false
              }
            }
          : row
      ));
      queueRef.current = nextQueue;
      setQueue(nextQueue);
      notify(detail);
    }
  }, [applyVariantResult, notify]);

  const applyVariantMetadata = useCallback(async (items) => {
    const chunks = [];
    for (let start = 0; start < items.length; start += VARIANT_DISCOVERY_CHUNK_SIZE) {
      chunks.push(items.slice(start, start + VARIANT_DISCOVERY_CHUNK_SIZE));
    }
    let index = 0;
    async function worker() {
      while (index < chunks.length) {
        const current = index;
        index += 1;
        await applyVariantMetadataChunk(chunks[current]);
      }
    }
    await Promise.all(Array.from({ length: Math.min(VARIANT_DISCOVERY_CONCURRENCY, chunks.length) }, worker));
  }, [applyVariantMetadataChunk]);

  const recordFetch = (entries) => {
    updateWorkspace({
      fetchHistory: [
        { id: `${Date.now()}-${Math.random()}`, fetchedAt: Date.now(), entries },
        ...(workspaceState.fetchHistory || [])
      ].slice(0, FETCH_HISTORY_LIMIT)
    });
  };

  const handleAddTopics = async () => {
    const parsed = parseTopicList(topicText);
    if (parsed.length === 0) {
      notify('No topics found in the input.');
      return;
    }

    try {
      setLookingUp(true);
      const entries = parsed.map(topic => ({
        title: topic,
        extract: '',
        url: '',
        source: 'manual',
        selected: true
      }));
      const newItems = addEntries(entries, { sort: false });
      recordFetch(entries);
      setTopicText('');
      await applyLibraryPresence(newItems);
      notify(`Added ${entries.length} ${entries.length === 1 ? 'entry' : 'entries'}.`);
    } catch (error) {
      notify(error.message);
    } finally {
      setLookingUp(false);
    }
  };

  const handleRandom = async () => {
    try {
      setFetchingRandom(true);
      const shuffledTopics = [...RANDOM_LEXICON_TOPICS].sort(() => Math.random() - 0.5);
      const entries = shuffledTopics.slice(0, fetchCount).map(topic => ({
        title: topic,
        extract: '',
        url: '',
        source: 'manual',
        selected: true
      }));
      const newItems = addEntries(entries, { sort: false });
      recordFetch(entries);
      await applyLibraryPresence(newItems);
      notify(`Added ${entries.length} random ${entries.length === 1 ? 'entry' : 'entries'}.`);
    } catch (error) {
      notify(error.message);
    } finally {
      setFetchingRandom(false);
    }
  };

  const searchVariants = async () => {
    if (searchingVariants || running) return;
    const pendingVariantItems = queueRef.current.filter(item => !item.variantChecked && item.status !== 'running');
    if (!pendingVariantItems.length) {
      notify('No unchecked topics need variant search.');
      return;
    }
    try {
      setSearchingVariants(true);
      startOperation?.({
        title: 'Searching variants',
        detail: 'Using OpenAI web search to classify topics and find optional variants.',
        duration: Math.max(8000, Math.ceil(pendingVariantItems.length / 20) * 8000),
        steps: ['Prepare topics', 'OpenAI search', 'Update queue'],
        indeterminate: true,
        statusLabel: 'Searching...'
      });
      await applyVariantMetadata(pendingVariantItems);
      notify(`Variant search complete for ${pendingVariantItems.length} ${pendingVariantItems.length === 1 ? 'topic' : 'topics'}.`);
    } catch (error) {
      notify(error.response?.data?.detail || error.message);
    } finally {
      setSearchingVariants(false);
      finishOperation?.();
    }
  };

  const addAllVariantsForItem = async (item) => {
    const variantEntries = (item.variantCandidates || []).filter(candidate => candidate.title);
    if (!variantEntries.length) {
      notify('No additional variants were found for this topic.');
      return;
    }
    const newItems = addEntries(variantEntries, { sort: true });
    await applyLibraryPresence(newItems);
    notify(`Added ${newItems.length} variant${newItems.length === 1 ? '' : 's'} for "${item.entry.title}".`);
  };

  const addSelectedVariantsForItem = async (item) => {
    const variantEntries = (item.variantCandidates || [])
      .filter(candidate => candidate.selectedForAdd !== false)
      .filter(candidate => candidate.title);
    if (!variantEntries.length) {
      notify('Select at least one variant to add.');
      return;
    }
    const newItems = addEntries(variantEntries, { sort: true });
    await applyLibraryPresence(newItems);
    notify(`Added ${newItems.length} selected variant${newItems.length === 1 ? '' : 's'} for "${item.entry.title}".`);
  };

  const toggleVariantCandidateSelected = (itemId, candidateTitle) => {
    const nextQueue = queueRef.current.map(item => {
      if (item.id !== itemId) return item;
      return {
        ...item,
        variantCandidates: (item.variantCandidates || []).map(candidate => (
          candidate.title === candidateTitle
            ? { ...candidate, selectedForAdd: candidate.selectedForAdd === false }
            : candidate
        ))
      };
    });
    queueRef.current = nextQueue;
    setQueue(nextQueue);
  };

  const addAllDiscoveredVariants = async () => {
    const existingTitles = new Set(queueRef.current.map(item => normalizeQueueTitle(item.entry.title)));
    const variantEntries = [];
    for (const item of queueRef.current) {
      for (const candidate of item.variantCandidates || []) {
        const key = normalizeQueueTitle(candidate.title);
        if (!candidate.title || existingTitles.has(key)) continue;
        existingTitles.add(key);
        variantEntries.push(candidate);
      }
    }
    if (!variantEntries.length) return [];
    const newItems = addEntries(variantEntries, { sort: true });
    await applyLibraryPresence(newItems);
    return newItems;
  };

  const removeQueueItem = (id) => {
    const nextQueue = queueRef.current.filter(item => item.id !== id);
    queueRef.current = nextQueue;
    setQueue(nextQueue);
  };

  const toggleQueueItemSelected = (id) => {
    const nextQueue = queueRef.current.map(item => (
      item.id === id ? { ...item, selected: !item.selected } : item
    ));
    queueRef.current = nextQueue;
    setQueue(nextQueue);
  };

  const clearQueue = () => {
    queueRef.current = [];
    setQueue([]);
    setRunUsage(null);
    setActiveJob(null);
    activeJobRef.current = null;
  };

  const removeLibraryDuplicates = () => {
    const removedCount = queueRef.current.filter(item => item.presentInLibrary || item.presentInEncye).length;
    const nextQueue = queueRef.current.filter(item => !item.presentInLibrary && !item.presentInEncye);
    queueRef.current = nextQueue;
    setQueue(nextQueue);
    notify(`Removed ${removedCount} existing ${removedCount === 1 ? 'entry' : 'entries'}.`);
  };

  const resetQueue = () => {
    const nextQueue = queueRef.current.map(item => ({
      ...item,
      status: 'pending',
      summary: undefined,
      error: undefined,
      usage: undefined
    }));
    queueRef.current = nextQueue;
    setQueue(nextQueue);
    setRunUsage(null);
    setActiveJob(null);
    activeJobRef.current = null;
  };

  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const fetchJobItems = async (jobId, total) => {
    const limit = 500;
    const pages = [];
    for (let offset = 0; offset < total; offset += limit) {
      const response = await lexiconAgentService.getGenerationJobItems(jobId, { offset, limit });
      pages.push(...(response.data.items || []));
      if ((response.data.items || []).length < limit) break;
    }
    return pages;
  };

  const mergeJobItems = (jobItems, pendingItems) => {
    const byIndex = new Map(jobItems.map(item => [item.index, item]));
    const byId = new Map(jobItems.map(item => [item._id, item]));
    const nextQueue = queueRef.current.map(item => {
      const pendingIndex = pendingItems.findIndex(pendingItem => pendingItem.id === item.id);
      const jobItem = item.generationItemId
        ? byId.get(item.generationItemId)
        : byIndex.get(item.generationIndex ?? pendingIndex);
      if (!jobItem) return item;
      return {
        ...item,
        status: jobItem.status === 'done' ? 'done' : jobItem.status,
        selected: jobItem.status === 'done' ? true : item.selected,
        summary: jobItem.summary || undefined,
        error: jobItem.error || undefined,
        usage: jobItem.usage,
        generationJobId: jobItem.jobId,
        generationItemId: jobItem._id,
        generationIndex: jobItem.index
      };
    });
    queueRef.current = nextQueue;
    setQueue(nextQueue);
  };

  const startQueue = async () => {
    if (running) return;
    const pendingItems = queueRef.current.filter(item => item.status === 'pending');
    if (!pendingItems.length) return;
    const existingPendingItems = pendingItems.filter(item => item.presentInLibrary || item.presentInEncye);
    if (existingPendingItems.length) {
      const shownTitles = existingPendingItems.map(item => item.entry.title).slice(0, 8);
      if (!canManageLexicons) {
        const removeTopics = await showBrandedConfirm({
          title: 'Existing lexicons cannot be generated',
          message: `You already have ${existingPendingItems.length} existing lexicon${existingPendingItems.length === 1 ? '' : 's'} in this queue, library, or Encye: ${shownTitles.join(', ')}${existingPendingItems.length > shownTitles.length ? ', ...' : ''}. Remove these topics to continue. You can later publish existing library lexicons from Library.`,
          confirmLabel: 'Remove topic',
          tone: 'warning'
        });
        if (removeTopics) removeLibraryDuplicates();
        return;
      }
      const confirmed = await showBrandedConfirm({
        title: 'Generate existing lexicons again?',
        message: `This queue contains ${existingPendingItems.length} lexicon${existingPendingItems.length === 1 ? '' : 's'} already in the library or Encye: ${shownTitles.join(', ')}${existingPendingItems.length > shownTitles.length ? ', ...' : ''}. You can generate them again, but saving/publishing later will ask you to override the existing version.`,
        confirmLabel: 'Generate again',
        tone: 'warning'
      });
      if (!confirmed) return;
    }

    stoppedRef.current = false;
    setRunUsage(null);
    setRunning(true);

    const runningIds = new Set(pendingItems.map(item => item.id));
    const runningQueue = queueRef.current.map(item => (
      runningIds.has(item.id)
        ? { ...item, status: 'running', error: undefined, usage: undefined }
        : item
    ));
    queueRef.current = runningQueue;
    setQueue(runningQueue);

    const topicsPayload = pendingItems.map(item => {
      const payload = { title: item.entry.title };
      const extract = clipBulkSourceExtract(item.entry.extract);
      if (extract) payload.extract = extract;
      if (item.entry.url) payload.sourceUrl = withSelegicUtm(item.entry.url);
      if (item.entry.source && item.entry.source !== 'manual') payload.source = item.entry.source;
      return payload;
    });

    const data = workspaceState.useCustomPrompt && workspaceState.customPrompt.trim()
      ? {
          topics: topicsPayload,
          mode: 'extract_based',
          length: workspaceState.length,
          prompt: workspaceState.customPrompt.trim()
        }
      : {
          topics: topicsPayload,
          mode: 'extract_based',
          length: workspaceState.length,
          style: workspaceState.style
        };

    try {
      const response = await lexiconAgentService.createGenerationJob(data);
      activeJobRef.current = response.data;
      setActiveJob(response.data);

      while (!stoppedRef.current) {
        const jobResponse = await lexiconAgentService.getGenerationJob(response.data._id);
        const job = jobResponse.data;
        activeJobRef.current = job;
        setActiveJob(job);
        setRunUsage(job.usage);

        const jobItems = await fetchJobItems(job._id, job.total || pendingItems.length);
        mergeJobItems(jobItems, pendingItems);

        if (['completed', 'completed_with_errors', 'failed', 'cancelled'].includes(job.status)) {
          break;
        }
        await sleep(2500);
      }
    } catch (error) {
      const handledLimit = await showLexiconLimitModal(error);
      const handledExisting = handledLimit ? false : await showExistingGenerationBlockedModal(error);
      const nextQueue = queueRef.current.map(item => (
        runningIds.has(item.id)
          ? {
              ...item,
              status: stoppedRef.current || handledLimit || handledExisting ? 'pending' : 'error',
              error: stoppedRef.current || handledLimit || handledExisting ? undefined : (error.response?.data?.detail || error.message),
              usage: undefined
            }
          : item
      ));
      queueRef.current = nextQueue;
      setQueue(nextQueue);
      if (handledLimit || handledExisting) {
        const restoredQueue = queueRef.current.map(item => (
          runningIds.has(item.id) ? { ...item, status: 'pending', error: undefined, usage: undefined } : item
        ));
        queueRef.current = restoredQueue;
        setQueue(restoredQueue);
      }
    } finally {
      setRunning(false);
    }
  };

  const generateWithAllVariants = async () => {
    if (running || searchingVariants || generatingWithVariants) return;
    const hasPending = queueRef.current.some(item => item.status === 'pending');
    if (!hasPending) {
      notify('No pending entries are ready for generation.');
      return;
    }

    try {
      setGeneratingWithVariants(true);
      const uncheckedItems = queueRef.current.filter(item => !item.variantChecked && item.status !== 'running');
      if (uncheckedItems.length) {
        setSearchingVariants(true);
        startOperation?.({
          title: 'Searching variants',
          detail: 'Using OpenAI web search to add all discovered variants before generation.',
          duration: Math.max(8000, Math.ceil(uncheckedItems.length / 20) * 8000),
          steps: ['Prepare topics', 'OpenAI search', 'Add variants'],
          indeterminate: true,
          statusLabel: 'Preparing...'
        });
        await applyVariantMetadata(uncheckedItems);
        finishOperation?.();
        setSearchingVariants(false);
      }

      const addedVariants = await addAllDiscoveredVariants();
      if (addedVariants.length) {
        notify(`Added ${addedVariants.length} discovered variant${addedVariants.length === 1 ? '' : 's'} before generation.`);
      }
      await startQueue();
    } catch (error) {
      notify(error.response?.data?.detail || error.message);
    } finally {
      setSearchingVariants(false);
      setGeneratingWithVariants(false);
      finishOperation?.();
    }
  };

  const stopQueue = async () => {
    stoppedRef.current = true;
    const jobId = activeJobRef.current?._id;
    if (jobId) {
      try {
        const response = await lexiconAgentService.cancelGenerationJob(jobId);
        activeJobRef.current = response.data;
        setActiveJob(response.data);
      } catch (error) {
        notify(error.response?.data?.detail || error.message);
      }
    }
  };

  const retryFailedQueue = async () => {
    const jobId = activeJobRef.current?._id || activeJob?._id;
    if (!jobId || running) return;
    try {
      stoppedRef.current = false;
      const retryResponse = await lexiconAgentService.retryFailedGenerationJob(jobId);
      const nextQueue = queueRef.current.map(item => (
        item.status === 'error'
          ? { ...item, status: 'running', error: undefined, summary: undefined }
          : item
      ));
      queueRef.current = nextQueue;
      setQueue(nextQueue);
      setRunning(true);
      activeJobRef.current = retryResponse.data;
      setActiveJob(retryResponse.data);
      const retryItems = nextQueue.filter(item => item.status === 'running');
      while (!stoppedRef.current) {
        const jobResponse = await lexiconAgentService.getGenerationJob(jobId);
        const job = jobResponse.data;
        activeJobRef.current = job;
        setActiveJob(job);
        setRunUsage(job.usage);
        const jobItems = await fetchJobItems(job._id, job.total || queueRef.current.length);
        mergeJobItems(jobItems, retryItems);
        if (['completed', 'completed_with_errors', 'failed', 'cancelled'].includes(job.status)) {
          break;
        }
        await sleep(2500);
      }
    } catch (error) {
      notify(error.response?.data?.detail || error.message);
    } finally {
      setRunning(false);
    }
  };

  const handleExport = () => {
    const data = queue.map(item => ({
      topic: item.entry.title,
      status: item.status,
      summary: item.summary
    }));
    exportToCsv(data, 'lexicon_queue_export.csv');
  };

  const saveSummary = async (item) => {
    if (savingIds.has(item.id)) return;
    try {
      setSavingIds(prev => new Set(prev).add(item.id));
      await saveLexiconArticle({
        title: item.entry.title,
        slug: slugify(item.entry.title),
        content: item.summary,
        sourceUrl: withSelegicUtm(item.entry.url),
        sourceType: item.entry.source || 'manual',
        allowVariants: Boolean(item.entry.variantGroupKey),
        variantGroupKey: item.entry.variantGroupKey,
        variantDetectionReason: item.entry.variantDetectionReason,
        published: true
      });
      removeQueueItem(item.id);
      notify(`Saved "${item.entry.title}"`);
    } catch (error) {
      notify(error.response?.data?.detail || error.message);
    } finally {
      setSavingIds(prev => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };

  const publishSummary = async (item) => {
    if (publishingIds.has(item.id) || item.encyeLexiconId) return;
    let duplicateDecision;
    let libraryDecision;
    try {
      libraryDecision = await confirmLibraryDuplicateAction([item], {
        canManageLexicons,
        getTitle: row => row.entry.title,
        removeItems: rows => {
          const ids = new Set(rows.map(row => row.id));
          const nextQueue = queueRef.current.filter(row => !ids.has(row.id));
          queueRef.current = nextQueue;
          setQueue(nextQueue);
        }
      });
      if (!libraryDecision.proceed) return;
      duplicateDecision = await confirmEncyeDuplicatePublish([item.entry.title], { canManageLexicons });
    } catch (error) {
      notify(error.response?.data?.detail || error.message);
      return;
    }
    if (!duplicateDecision.proceed) return;
    try {
      setPublishingIds(prev => new Set(prev).add(item.id));
      startOperation?.({
        title: 'Publishing lexicon to Encye',
        detail: 'Preparing the lexicon, linking variants, and saving it to Encye.',
        duration: 14000,
        steps: ['Prepare entry', 'Create lexicon', 'Link variants', 'Confirm publish']
      });
      let articleId = item.articleId;
      if (!articleId) {
        const saved = await saveLexiconArticle({
          title: item.entry.title,
          slug: slugify(item.entry.title),
          content: item.summary,
          sourceUrl: withSelegicUtm(item.entry.url),
          sourceType: item.entry.source || 'manual',
          allowVariants: Boolean(item.entry.variantGroupKey),
          variantGroupKey: item.entry.variantGroupKey,
          variantDetectionReason: item.entry.variantDetectionReason,
          published: true,
          overrideExisting: libraryDecision.overrideExisting
        });
        articleId = saved.data._id;
        setQueue(prev => prev.map(row => row.id === item.id ? { ...row, articleId } : row));
      }
      const response = await publishLexiconArticleToEncye(articleId, {
        canManageLexicons,
        overrideEncye: duplicateDecision.overrideEncye
      });
      const published = response.data.article;
      setQueue(prev => prev.map(row => row.id === item.id ? {
        ...row,
        articleId,
        encyeLexiconId: published.encyeLexiconId,
        encyePublishedAt: published.encyePublishedAt
      } : row));
      notify(`Published "${item.entry.title}" to Encye`);
    } catch (error) {
      notify(error.response?.data?.detail || error.message);
    } finally {
      setPublishingIds(prev => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
      finishOperation?.();
    }
  };

  const publishAllSummaries = async () => {
    const pending = queue.filter(item => item.selected !== false && item.status === 'done' && item.summary && !item.encyeLexiconId);
    if (!pending.length || bulkPublishing) return;
    let libraryDecision;
    let duplicateDecision;
    try {
      libraryDecision = await confirmLibraryDuplicateAction(pending, {
        canManageLexicons,
        getTitle: item => item.entry.title,
        removeItems: rows => {
          const ids = new Set(rows.map(row => row.id));
          const nextQueue = queueRef.current.filter(row => !ids.has(row.id));
          queueRef.current = nextQueue;
          setQueue(nextQueue);
        }
      });
      if (!libraryDecision.proceed) return;
      duplicateDecision = await confirmEncyeDuplicatePublish(pending.map(item => item.entry.title), { canManageLexicons });
    } catch (error) {
      notify(error.response?.data?.detail || error.message);
      return;
    }
    if (!duplicateDecision.proceed) return;
    let publishedCount = 0;
    let failedCount = 0;
    let firstError = '';
    setBulkPublishing(true);
    startOperation?.({
      title: 'Publishing selected lexicons to Encye',
      detail: 'Uploading selected lexicons, connecting variants, and confirming Encye records.',
      duration: Math.max(16000, pending.length * 5000),
      steps: ['Prepare batch', 'Upload lexicons', 'Link variants', 'Finalize']
    });
    setPublishingIds(prev => new Set([...prev, ...pending.map(item => item.id)]));
    for (const item of pending) {
      try {
        let articleId = item.articleId;
        if (!articleId) {
          const saved = await saveLexiconArticle({
            title: item.entry.title,
            slug: slugify(item.entry.title),
            content: item.summary,
            sourceUrl: withSelegicUtm(item.entry.url),
            sourceType: item.entry.source || 'manual',
            allowVariants: Boolean(item.entry.variantGroupKey),
            variantGroupKey: item.entry.variantGroupKey,
            variantDetectionReason: item.entry.variantDetectionReason,
            published: true,
            overrideExisting: libraryDecision.overrideExisting
          });
          articleId = saved.data._id;
          setQueue(prev => prev.map(row => row.id === item.id ? { ...row, articleId } : row));
        }
        const response = await publishLexiconArticleToEncye(articleId, {
          canManageLexicons,
          overrideEncye: duplicateDecision.overrideEncye
        });
        const published = response.data.article;
        setQueue(prev => prev.map(row => row.id === item.id ? {
          ...row,
          articleId,
          encyeLexiconId: published.encyeLexiconId,
          encyePublishedAt: published.encyePublishedAt
        } : row));
        publishedCount += 1;
      } catch (error) {
        failedCount += 1;
        const detail = error.response?.data?.detail || error.response?.data?.error || error.message;
        if (!firstError) firstError = detail;
        setQueue(prev => prev.map(row => row.id === item.id ? { ...row, error: detail } : row));
      } finally {
        setPublishingIds(prev => {
          const next = new Set(prev);
          next.delete(item.id);
          return next;
        });
      }
    }
    setBulkPublishing(false);
    finishOperation?.();
    notify(`Bulk publish complete: ${publishedCount} published${failedCount ? `, ${failedCount} failed: ${firstError}` : ''}.`);
  };

  const saveSelectedSummaries = async () => {
    const pending = queue.filter(item => item.selected !== false && item.status === 'done' && item.summary && !item.articleId);
    if (!pending.length) return;
    let savedCount = 0;
    for (const item of pending) {
      try {
        const response = await saveLexiconArticle({
          title: item.entry.title,
          slug: slugify(item.entry.title),
          content: item.summary,
          sourceUrl: withSelegicUtm(item.entry.url),
          sourceType: item.entry.source || 'manual',
          allowVariants: Boolean(item.entry.variantGroupKey),
          variantGroupKey: item.entry.variantGroupKey,
          variantDetectionReason: item.entry.variantDetectionReason,
          published: true
        });
        savedCount += 1;
        setQueue(prev => prev.map(row => row.id === item.id ? { ...row, articleId: response.data._id } : row));
      } catch (error) {
        const detail = error.response?.data?.detail || error.message;
        setQueue(prev => prev.map(row => row.id === item.id ? { ...row, error: detail } : row));
      }
    }
    notify(`Saved ${savedCount} selected variant${savedCount === 1 ? '' : 's'}.`);
  };

  const pendingCount = queue.filter(item => item.status === 'pending').length;
  const runningCount = queue.filter(item => item.status === 'running').length;
  const errorCount = queue.filter(item => item.status === 'error').length;
  const finishedCount = queue.filter(item => item.status === 'done' || item.status === 'error').length;
  const libraryDuplicateCount = queue.filter(item => item.presentInLibrary || item.presentInEncye).length;
  const uncheckedVariantCount = queue.filter(item => !item.variantChecked && item.status !== 'running').length;
  const fetchHistory = workspaceState.fetchHistory || [];

  return (
    <div className="lexicon-workspace-grid">
      <div className="lexicon-column">
        <SummaryOptions
          options={options}
          values={{
            style: workspaceState.style,
            length: workspaceState.length,
            useCustomPrompt: workspaceState.useCustomPrompt,
            customPrompt: workspaceState.customPrompt
          }}
          onChange={(patch) => updateWorkspace({
            style: patch.style ?? workspaceState.style,
            length: patch.length ?? workspaceState.length,
            useCustomPrompt: patch.useCustomPrompt ?? workspaceState.useCustomPrompt,
            customPrompt: patch.customPrompt ?? workspaceState.customPrompt
          })}
        />
        <div className="lexicon-panel">
          <div className="lexicon-panel-header">
            <h3>Add entries</h3>
            <p>Paste topics, one per line. Search variants from the queue when you are ready.</p>
          </div>
          <textarea
            rows="5"
            value={topicText}
            onChange={(event) => setTopicText(event.target.value)}
            placeholder={'Black hole\nPhotosynthesis\nMars, planet'}
          />
          <div className="lexicon-inline-actions">
            <button className="btn btn-primary" onClick={handleAddTopics} disabled={!topicText.trim() || lookingUp}>
              {lookingUp ? 'Adding...' : 'Add topics'}
            </button>
            <input
              className="lexicon-small-input"
              type="number"
              min="1"
              max="10"
              value={fetchCount}
              onChange={(event) => setFetchCount(Math.max(1, Math.min(10, Number(event.target.value))))}
            />
            <button className="btn btn-secondary" onClick={handleRandom} disabled={fetchingRandom}>
              {fetchingRandom ? `Adding ${fetchCount}...` : `Add ${fetchCount} random`}
            </button>
          </div>
        </div>
      </div>

      <div className="lexicon-column lexicon-main-column">
        <div className="lexicon-panel">
          <div className="lexicon-panel-toolbar-with-stats">
            <div className="lexicon-toolbar-header-row">
              <div className="lexicon-queue-heading">
                <h3>Generation queue</h3>
                <p>Generate, review, and publish your lexicon entries.</p>
              </div>
              <div className="lexicon-queue-stats">
                <span className="lexicon-stat-item">
                  <FileText size={14} /> {queue.length} {queue.length === 1 ? 'entry' : 'entries'}
                </span>
                <span className="lexicon-stat-item">
                  <CheckSquare size={14} /> {pendingCount} pending
                </span>
                {runningCount > 0 && (
                  <span className="lexicon-stat-item">
                    <Play size={14} /> {runningCount} running
                  </span>
                )}
                {errorCount > 0 && (
                  <span className="lexicon-stat-item">
                    <RotateCcw size={14} /> {errorCount} failed
                  </span>
                )}
                <TokenUsageStat usage={runUsage} itemCount={queue.filter(item => item.status === 'done' || item.status === 'error').length || queue.length} />
              </div>
            </div>
            <div className="lexicon-queue-actionbar" aria-label="Generation queue actions">
              <div className="lexicon-command-section lexicon-command-section-primary">
                <span className="lexicon-command-label">Generate</span>
                <div className="lexicon-command-buttons">
                  {running ? (
                    <button className="btn btn-danger btn-toolbar-start" onClick={stopQueue}>Stop</button>
                  ) : (
                    <>
                      <button className="btn btn-primary btn-toolbar-start" onClick={startQueue} disabled={pendingCount === 0}>
                        <Play size={14} fill="currentColor" /> Start
                      </button>
                      <button
                        className="btn btn-secondary btn-toolbar-rerun"
                        onClick={resetQueue}
                        disabled={finishedCount === 0}
                      >
                        <RotateCcw size={14} /> Rerun
                      </button>
                      {errorCount > 0 && (
                        <button
                          className="btn btn-secondary btn-toolbar-rerun"
                          onClick={retryFailedQueue}
                          disabled={!activeJob}
                        >
                          <RotateCcw size={14} /> Retry errors
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="lexicon-command-section lexicon-command-section-variants">
                <span className="lexicon-command-label">Variants</span>
                <div className="lexicon-command-buttons">
                  <button className="btn btn-outline btn-toolbar-search" onClick={searchVariants} disabled={running || searchingVariants || uncheckedVariantCount === 0}>
                    <Search size={14} /> {searchingVariants ? 'Searching...' : `Search variants${uncheckedVariantCount ? ` (${uncheckedVariantCount})` : ''}`}
                  </button>
                  <button
                    className="btn btn-primary btn-generate-with-variants"
                    onClick={generateWithAllVariants}
                    disabled={running || searchingVariants || generatingWithVariants || pendingCount === 0}
                    title="Search unchecked variants, add all discovered variants, then generate summaries"
                  >
                    <PlayCircle size={14} /> {generatingWithVariants ? 'Preparing...' : 'Discover & Generate'}
                  </button>
                </div>
              </div>

              <div className="lexicon-command-section lexicon-command-section-cleanup">
                <span className="lexicon-command-label">Queue</span>
                <div className="lexicon-command-buttons">
                  <button className="btn btn-outline btn-toolbar-clear" onClick={clearQueue} disabled={running || queue.length === 0}>
                    <Trash2 size={14} /> Clear
                  </button>
                  <button className="btn btn-outline btn-toolbar-clear" onClick={removeLibraryDuplicates} disabled={running || libraryDuplicateCount === 0}>
                    <Trash2 size={14} /> Remove topic
                  </button>
                </div>
              </div>

              <div className="lexicon-command-section lexicon-command-section-utilities">
                <span className="lexicon-command-label">Tools</span>
                <div className="lexicon-command-buttons">
                  <button className="btn btn-outline btn-toolbar-history" onClick={() => setHistoryOpen(true)}>
                    <History size={14} /> History
                  </button>
                  <button className="btn btn-outline btn-toolbar-export" onClick={handleExport} disabled={queue.length === 0}>
                    <Download size={14} /> Export
                  </button>
                </div>
              </div>

              {queue.some(item => item.status === 'done' && item.summary && !item.encyeLexiconId) && (
                <div className="lexicon-command-section lexicon-command-section-save">
                  <span className="lexicon-command-label">Save</span>
                  <div className="lexicon-command-buttons">
                    <button
                      className="btn btn-outline btn-toolbar-save"
                      onClick={saveSelectedSummaries}
                      disabled={!queue.some(item => item.selected !== false && item.status === 'done' && item.summary && !item.articleId)}
                    >
                      <CheckSquare size={14} /> Save selected
                    </button>
                    <button
                      className="btn btn-primary btn-bulk-publish"
                      onClick={publishAllSummaries}
                      disabled={bulkPublishing}
                    >
                      <Ship size={14} /> {bulkPublishing ? 'Uploading...' : 'Bulk upload to Encye'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          {queue.length === 0 ? (
            <div className="lexicon-empty lexicon-queue-empty">
              <div className="lexicon-empty-icon"><BookOpen size={22} /></div>
              <h4>Your queue is ready</h4>
              <p>Add topics, search variants when needed, then select Start to generate summaries.</p>
            </div>
          ) : (
            <div className="lexicon-queue-cards-feed">
              {queue.map((item, index) => {
                const CardIcon = getCardIcon(item.entry.title);
                const isCollapsed = collapsedCards.has(item.id);
                const variantsOpen = expandedVariantPickers.has(item.id);
                const wordCount = item.summary ? item.summary.trim().split(/\s+/).length : 0;

                return (
                  <div key={item.id} className={`lexicon-queue-card status-${item.status}${item.presentInLibrary ? ' present-in-library' : ''}`}>
                    {/* Main Card Content */}
                    <div className="lexicon-qcard-main-section">
                      <div className="lexicon-qcard-title-row">
                        <div className="lexicon-qcard-title-left">
                          {(item.isVariant || item.entry.variantGroupKey) && (
                            <input
                              type="checkbox"
                              className="lexicon-variant-checkbox"
                              checked={item.selected !== false}
                              onChange={() => toggleQueueItemSelected(item.id)}
                              disabled={Boolean(item.articleId) || Boolean(item.encyeLexiconId)}
                              title="Select this variant for save or publish"
                            />
                          )}
                          <div className="lexicon-qcard-icon-wrapper-small">
                            <CardIcon size={16} />
                          </div>
                          <div className="lexicon-qcard-title-stack">
                            <h4>{index + 1}. {item.entry.title}</h4>
                            <div className="lexicon-qcard-tags" aria-label="Queue item labels">
                              {item.presentInLibrary && (
                                <span className="lexicon-status lexicon-status-library">In library</span>
                              )}
                              {item.presentInEncye && (
                                <span className="lexicon-status lexicon-status-done">In Encye</span>
                              )}
                              {(item.isVariant || item.entry.variantGroupKey) && (
                                <span className="lexicon-status lexicon-status-running">Variant</span>
                              )}
                              {item.variantChecked && !(item.isVariant || item.entry.variantGroupKey) && (
                                <span className="lexicon-status lexicon-status-pending">Not variant</span>
                              )}
                              {item.entry.needsReview && (
                                <span className="lexicon-status lexicon-status-error">Review</span>
                              )}
                              <span className={`lexicon-status-badge status-${item.status}`}>
                                {item.status === 'done' ? 'Done' : item.status}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="lexicon-qcard-actions">
                          {(item.isVariant || item.entry.variantGroupKey) && (item.variantCandidates || []).length > 0 && (
                            <>
                              <button
                                className="btn btn-outline btn-sm"
                                onClick={() => toggleVariantPicker(item.id)}
                                disabled={item.status === 'running'}
                                title="Show discovered variants"
                              >
                                {variantsOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Variants ({item.variantCandidates.length})
                              </button>
                            </>
                          )}
                          {item.status === 'done' && (
                            <>
                              <button
                                className="btn btn-outline btn-sm btn-save"
                                onClick={() => saveSummary(item)}
                                disabled={savingIds.has(item.id) || Boolean(item.articleId)}
                              >
                                <Download size={12} /> {item.articleId ? 'Saved' : savingIds.has(item.id) ? 'Saving' : 'Save to Library'}
                              </button>
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() => publishSummary(item)}
                                disabled={publishingIds.has(item.id) || Boolean(item.encyeLexiconId)}
                              >
                                <Ship size={12} /> {item.encyeLexiconId ? 'In Encye' : publishingIds.has(item.id) ? 'Publishing...' : 'Publish to Encye'}
                              </button>
                            </>
                          )}
                          {item.status !== 'running' && (
                            <button className="btn btn-outline btn-sm btn-remove" onClick={() => removeQueueItem(item.id)}>
                              <Trash size={12} /> Remove
                            </button>
                          )}
                          <button className="lexicon-qcard-collapse-btn" onClick={() => toggleCardCollapse(item.id)}>
                            {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                          </button>
                        </div>
                      </div>

                      {/* Collapsible Area */}
                      {!isCollapsed && (
                        <>
                          <div className="lexicon-qcard-body">
                            {item.status === 'running' && (
                              <div className="lexicon-card-loading">
                                <div className="lexicon-card-spinner"></div>
                                <span>Summarizing topic using AI agent...</span>
                              </div>
                            )}
                            {item.status === 'pending' && (
                              <div className="lexicon-card-pending-msg">
                                Ready for generation. Click <strong>Start</strong> in the toolbar above to generate the summary.
                              </div>
                            )}
                            {item.variantSearchError && (
                              <div className="lexicon-card-error-msg">
                                Variant search failed: {item.variantSearchError}
                              </div>
                            )}
                            {variantsOpen && (item.variantCandidates || []).length > 0 && (
                              <div className="lexicon-variant-picker">
                                <div className="lexicon-variant-picker-header">
                                  <strong>Choose variants to add</strong>
                                  <div>
                                    <button className="btn btn-outline btn-sm" onClick={() => addSelectedVariantsForItem(item)}>
                                      <CheckSquare size={12} /> Add selected
                                    </button>
                                    <button className="btn btn-primary btn-sm" onClick={() => addAllVariantsForItem(item)}>
                                      <ListPlus size={12} /> Add all
                                    </button>
                                  </div>
                                </div>
                                <div className="lexicon-variant-picker-list">
                                  {(item.variantCandidates || []).map(candidate => (
                                    <label className="lexicon-variant-picker-row" key={`${item.id}-${candidate.title}`}>
                                      <input
                                        type="checkbox"
                                        checked={candidate.selectedForAdd !== false}
                                        onChange={() => toggleVariantCandidateSelected(item.id, candidate.title)}
                                      />
                                      <span>
                                        <strong>{candidate.title}</strong>
                                        <small>{candidate.extract || 'Variant candidate'}</small>
                                      </span>
                                      {candidate.confidence !== undefined && (
                                        <em>{matchPercentage(candidate)}%</em>
                                      )}
                                    </label>
                                  ))}
                                </div>
                              </div>
                            )}
                            {item.status === 'done' && item.summary && (
                              <div className="lexicon-qcard-summary">
                                <SummaryPreview text={item.summary} />
                              </div>
                            )}
                            {item.status === 'error' && (
                              <div className="lexicon-qcard-error">
                                {item.error}
                              </div>
                            )}
                            {item.status === 'cancelled' && (
                              <div className="lexicon-card-pending-msg">
                                Generation was cancelled.
                              </div>
                            )}
                          </div>

                          <div className="lexicon-qcard-footer">
                            {item.summary && (
                              <span className="lexicon-qcard-word-count">
                                {wordCount} words
                              </span>
                            )}
                            {item.entry.confidence !== undefined && (
                              <span className="lexicon-qcard-word-count">
                                {Math.round(Number(item.entry.confidence || 0) * 100)}% match
                              </span>
                            )}
                            {/* Views are used for ordering but hidden from the card UI. */}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {historyOpen && (
        <div className="lexicon-history-backdrop" onClick={() => setHistoryOpen(false)}>
          <div className="lexicon-history-drawer" onClick={(event) => event.stopPropagation()}>
            <div className="lexicon-history-drawer-header">
              <div>
                <h3>Fetch history</h3>
                <p>Re-queue a previous batch when you need it.</p>
              </div>
              <button className="btn btn-outline" onClick={() => setHistoryOpen(false)}>Close</button>
            </div>
            <div className="lexicon-history-drawer-actions">
              <button
                className="btn btn-outline"
                onClick={() => updateWorkspace({ fetchHistory: [] })}
                disabled={fetchHistory.length === 0}
              >
                Clear history
              </button>
            </div>
            <div className="lexicon-list-stack">
              {fetchHistory.map(batch => (
                <button
                  key={batch.id}
                  className="lexicon-history-row"
                  onClick={() => {
                    addEntries(batch.entries);
                    setHistoryOpen(false);
                  }}
                >
                  <strong>{batch.entries.length} article{batch.entries.length === 1 ? '' : 's'}</strong>
                  <span>{new Date(batch.fetchedAt).toLocaleString()}</span>
                  <small>{batch.entries.map(entry => entry.title).join(', ')}</small>
                </button>
              ))}
              {fetchHistory.length === 0 && <div className="lexicon-empty">Past fetches appear here.</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BatchSummarizer({ options, workspaceState, updateWorkspace, notify, canManageLexicons, startOperation, finishOperation }) {
  const [topics, setTopics] = useState([]);
  const [results, setResults] = useState([]);
  const [usage, setUsage] = useState(null);
  const [csvText, setCsvText] = useState('');
  const [running, setRunning] = useState(false);
  const [searchingVariants, setSearchingVariants] = useState(false);
  const [publishingTopics, setPublishingTopics] = useState(new Set());
  const [bulkPublishing, setBulkPublishing] = useState(false);
  const topicsRef = useRef(topics);

  useEffect(() => {
    topicsRef.current = topics;
  }, [topics]);

  const pendingVariantCount = topics.filter(item => !item.variantChecked).length;
  const existingCount = topics.filter(item => item.presentInLibrary || item.presentInEncye).length;
  const variantCount = topics.filter(item => item.isVariant).length;

  const addTopics = (incoming) => {
    const newItems = incoming.map(topic => (
      typeof topic === 'string'
        ? {
            id: `${Date.now()}-${Math.random()}`,
            title: topic,
            variantChecked: false,
            variantCandidates: [],
            status: 'pending'
          }
        : {
            id: `${Date.now()}-${Math.random()}`,
            variantChecked: Boolean(topic.variantChecked || topic.variantGroupKey),
            variantCandidates: topic.variantCandidates || [],
            status: 'pending',
            ...topic
          }
    ));
    const next = [...topicsRef.current, ...newItems];
    topicsRef.current = next;
    setTopics(next);
    return newItems;
  };

  const applyLibraryPresence = useCallback(async (items) => {
    const ids = new Set((items || []).map(item => item.id));
    const targetItems = topicsRef.current.filter(item => !ids.size || ids.has(item.id));
    const titles = targetItems.map(item => item.title);
    if (!titles.length) return;
    try {
      const matches = new Map();
      const encyeMatches = new Map();
      for (let start = 0; start < titles.length; start += LIBRARY_CHECK_CHUNK_SIZE) {
        const chunk = titles.slice(start, start + LIBRARY_CHECK_CHUNK_SIZE);
        const [response, encyeResponse] = await Promise.all([
          lexiconAgentService.checkExistingArticles({ titles: chunk }),
          lexiconAgentService.checkExistingEncyeLexicons({ titles: chunk }).catch(error => {
            if (isMissingEncyeCheck(error)) return { data: { matches: [] } };
            throw error;
          })
        ]);
        for (const match of response.data.matches || []) {
          matches.set(match.normalizedTitle, match);
        }
        for (const match of encyeResponse.data.matches || []) {
          encyeMatches.set(normalizeQueueTitle(match.title), match);
        }
      }
      const next = topicsRef.current.map(item => {
        if (ids.size && !ids.has(item.id)) return item;
        const match = matches.get(normalizeQueueTitle(item.title));
        const encyeMatch = encyeMatches.get(normalizeQueueTitle(item.title));
        return {
          ...item,
          presentInLibrary: Boolean(match),
          libraryArticleId: match?.articleId || null,
          presentInEncye: Boolean(match?.encyeLexiconId || encyeMatch),
          encyeExistingLexiconId: match?.encyeLexiconId || encyeMatch?.encyeLexiconId || null
        };
      });
      topicsRef.current = next;
      setTopics(next);
    } catch (error) {
      notify(error.response?.data?.detail || error.message);
    }
  }, [notify]);

  const candidateToBatchTopic = (candidate, topic, groupKey) => ({
    title: candidate.title,
    extract: '',
    url: '',
    source: 'manual',
    descriptionHint: candidate.descriptionHint || '',
    confidence: candidate.confidence,
    needsReview: candidate.needsReview,
    isVariant: true,
    variantChecked: true,
    variantGroupKey: groupKey,
    variantDetectionReason: `Discovered as a variant for "${topic}"`
  });

  const applyVariantResult = useCallback((item, result) => {
    const groupKey = result?.variantGroupKey;
    const variantCandidates = (result?.candidates || [])
      .filter(candidate => normalizeQueueTitle(candidate.title) !== normalizeQueueTitle(item.title))
      .map(candidate => ({ ...candidateToBatchTopic(candidate, item.title, groupKey), selectedForAdd: true }));
    const hasVariants = Boolean(result?.isVariantTopic) && variantCandidates.length > 0;
    const next = topicsRef.current.map(row => (
      row.id === item.id
        ? {
            ...row,
            isVariant: hasVariants,
            variantChecked: true,
            variantGroupKey: hasVariants ? groupKey : row.variantGroupKey,
            variantCandidates,
            variantSearchError: undefined
          }
        : row
    ));
    topicsRef.current = next;
    setTopics(next);
  }, []);

  const searchVariants = async () => {
    if (searchingVariants || running) return;
    const unchecked = topicsRef.current.filter(item => !item.variantChecked);
    if (!unchecked.length) {
      notify('No unchecked topics need variant search.');
      return;
    }
    try {
      setSearchingVariants(true);
      startOperation?.({
        title: 'Searching mass variants',
        detail: 'Classifying bulk topics and finding optional variants before generation.',
        duration: Math.max(8000, Math.ceil(unchecked.length / 20) * 8000),
        steps: ['Prepare topics', 'OpenAI search', 'Mark variants'],
        indeterminate: true,
        statusLabel: 'Searching...'
      });
      const chunks = [];
      for (let start = 0; start < unchecked.length; start += VARIANT_DISCOVERY_CHUNK_SIZE) {
        chunks.push(unchecked.slice(start, start + VARIANT_DISCOVERY_CHUNK_SIZE));
      }
      let index = 0;
      async function worker() {
        while (index < chunks.length) {
          const current = index;
          index += 1;
          const response = await lexiconAgentService.discoverVariantsBatch({ topics: chunks[current].map(item => item.title) });
          const byTopic = new Map((response.data.results || []).map(result => [normalizeQueueTitle(result.topic), result]));
          for (const item of chunks[current]) {
            applyVariantResult(item, byTopic.get(normalizeQueueTitle(item.title)));
          }
        }
      }
      await Promise.all(Array.from({ length: Math.min(VARIANT_DISCOVERY_CONCURRENCY, chunks.length) }, worker));
      notify(`Variant search complete for ${unchecked.length} ${unchecked.length === 1 ? 'topic' : 'topics'}.`);
    } catch (error) {
      notify(error.response?.data?.detail || error.message);
    } finally {
      setSearchingVariants(false);
      finishOperation?.();
    }
  };

  const addAllVariants = async (item) => {
    const variants = (item.variantCandidates || []).filter(candidate => candidate.title);
    if (!variants.length) return;
    const newItems = addTopics(variants);
    await applyLibraryPresence(newItems);
    notify(`Added ${newItems.length} variant${newItems.length === 1 ? '' : 's'} for "${item.title}".`);
  };

  const handleAddCsv = async () => {
    const parsed = parseTopicList(csvText);
    if (!parsed.length) {
      notify('No topics found in the input.');
      return;
    }
    const newItems = addTopics(parsed);
    setCsvText('');
    await applyLibraryPresence(newItems);
    notify(`Added ${newItems.length} bulk ${newItems.length === 1 ? 'topic' : 'topics'}.`);
  };

  const summarizeBatch = async () => {
    const pending = topicsRef.current.filter(item => item.status !== 'done');
    if (!pending.length) return;
    const existingPending = pending.filter(item => item.presentInLibrary || item.presentInEncye);
    if (existingPending.length) {
      const shownTitles = existingPending.map(item => item.title).slice(0, 8);
      if (!canManageLexicons) {
        const removeTopics = await showBrandedConfirm({
          title: 'Existing lexicons cannot be generated',
          message: `These already exist in the library or Encye: ${shownTitles.join(', ')}${existingPending.length > shownTitles.length ? ', ...' : ''}. Remove these topics to continue. You can later publish existing library lexicons from Library.`,
          confirmLabel: 'Remove topic',
          tone: 'warning'
        });
        if (removeTopics) {
          const next = topicsRef.current.filter(item => !item.presentInLibrary && !item.presentInEncye);
          topicsRef.current = next;
          setTopics(next);
        }
        return;
      }
      const confirmed = await showBrandedConfirm({
        title: 'Generate existing lexicons again?',
        message: `This mass batch contains ${existingPending.length} existing lexicon${existingPending.length === 1 ? '' : 's'} in the library or Encye. Saving/publishing later will ask before overriding existing descriptions.`,
        confirmLabel: 'Generate again',
        tone: 'warning'
      });
      if (!confirmed) return;
    }

    const topicsPayload = pending.map(item => {
      const payload = { title: item.title };
      const extract = clipBulkSourceExtract(item.extract);
      if (extract) payload.extract = extract;
      if (item.url) payload.sourceUrl = withSelegicUtm(item.url);
      if (item.source && item.source !== 'manual') payload.source = item.source;
      return payload;
    });
    const data = workspaceState.batchUseCustomPrompt && workspaceState.batchCustomPrompt.trim()
      ? {
          topics: topicsPayload,
          mode: 'extract_based',
          length: workspaceState.batchLength,
          prompt: workspaceState.batchCustomPrompt.trim()
        }
      : {
          topics: topicsPayload,
          mode: 'extract_based',
          length: workspaceState.batchLength,
          style: workspaceState.batchStyle
        };

    try {
      setRunning(true);
      const runningIds = new Set(pending.map(item => item.id));
      const started = topicsRef.current.map(item => runningIds.has(item.id) ? { ...item, status: 'running', error: undefined } : item);
      topicsRef.current = started;
      setTopics(started);
      startOperation?.({
        title: 'Mass generating lexicons',
        detail: 'Generating the selected bulk topics. You can keep reviewing the queue after this finishes.',
        duration: Math.max(12000, pending.length * 2500),
        steps: ['Create job', 'Generate items', 'Collect summaries'],
        indeterminate: true,
        statusLabel: 'Generating...'
      });
      const response = await lexiconAgentService.createGenerationJob(data);
      let job = response.data;
      while (true) {
        const jobResponse = await lexiconAgentService.getGenerationJob(job._id);
        job = jobResponse.data;
        setUsage(job.usage);
        const itemsResponse = await lexiconAgentService.getGenerationJobItems(job._id, { offset: 0, limit: Math.max(500, pending.length) });
        const jobItems = itemsResponse.data.items || [];
        const byIndex = new Map(jobItems.map(item => [item.index, item]));
        const nextTopics = topicsRef.current.map(item => {
          const pendingIndex = pending.findIndex(row => row.id === item.id);
          const jobItem = byIndex.get(pendingIndex);
          if (!jobItem) return item;
          return {
            ...item,
            status: jobItem.status === 'done' ? 'done' : jobItem.status,
            selected: jobItem.status === 'done' ? true : item.selected,
            summary: jobItem.summary || item.summary,
            error: jobItem.error,
            usage: jobItem.usage
          };
        });
        topicsRef.current = nextTopics;
        setTopics(nextTopics);
        setResults(nextTopics.filter(item => item.summary).map(item => ({
          topic: item.title,
          summary: item.summary,
          articleId: item.articleId,
          encyeLexiconId: item.encyeLexiconId,
          variantGroupKey: item.variantGroupKey,
          presentInLibrary: item.presentInLibrary,
          presentInEncye: item.presentInEncye,
          libraryArticleId: item.libraryArticleId,
          selected: true
        })));
        if (['completed', 'completed_with_errors', 'failed', 'cancelled'].includes(job.status)) break;
        await new Promise(resolve => setTimeout(resolve, 2500));
      }
    } catch (error) {
      const handledLimit = await showLexiconLimitModal(error);
      const handledExisting = handledLimit ? false : await showExistingGenerationBlockedModal(error);
      if (!handledLimit && !handledExisting) notify(error.response?.data?.detail || error.message);
      const next = topicsRef.current.map(item => item.status === 'running' ? { ...item, status: 'pending', error: undefined } : item);
      topicsRef.current = next;
      setTopics(next);
    } finally {
      setRunning(false);
      finishOperation?.();
    }
  };

  const saveResult = async (result) => {
    try {
      const response = await saveLexiconArticle({
        title: result.topic,
        slug: slugify(result.topic),
        content: result.summary,
        published: true
      });
      setResults(prev => prev.map(row => row === result ? { ...row, articleId: response.data._id } : row));
      notify(`Saved "${result.topic}"`);
    } catch (error) {
      notify(error.response?.data?.detail || error.message);
    }
  };

  const publishResult = async (result) => {
    if (publishingTopics.has(result.topic) || result.encyeLexiconId) return;
    let duplicateDecision;
    let libraryDecision;
    try {
      libraryDecision = await confirmLibraryDuplicateAction([result], {
        canManageLexicons,
        getTitle: row => row.topic,
        removeItems: rows => {
          const topicsToRemove = new Set(rows.map(row => normalizeQueueTitle(row.topic)));
          setResults(prev => prev.filter(row => !topicsToRemove.has(normalizeQueueTitle(row.topic))));
        }
      });
      if (!libraryDecision.proceed) return;
      duplicateDecision = await confirmEncyeDuplicatePublish([result.topic], { canManageLexicons });
    } catch (error) {
      notify(error.response?.data?.detail || error.message);
      return;
    }
    if (!duplicateDecision.proceed) return;
    try {
      setPublishingTopics(prev => new Set(prev).add(result.topic));
      startOperation?.({
        title: 'Publishing lexicon to Encye',
        detail: 'Saving the generated lexicon and publishing it to Encye.',
        duration: 14000,
        steps: ['Save lexicon', 'Upload to Encye', 'Confirm publish']
      });
      let articleId = result.articleId;
      if (!articleId) {
        const saved = await saveLexiconArticle({
          title: result.topic,
          slug: slugify(result.topic),
          content: result.summary,
          published: true,
          overrideExisting: libraryDecision.overrideExisting
        });
        articleId = saved.data._id;
        setResults(prev => prev.map(row => row === result ? { ...row, articleId } : row));
      }
      const response = await publishLexiconArticleToEncye(articleId, {
        canManageLexicons,
        overrideEncye: duplicateDecision.overrideEncye
      });
      setResults(prev => prev.map(row => row.topic === result.topic ? {
        ...row,
        articleId,
        encyeLexiconId: response.data.article.encyeLexiconId
      } : row));
      notify(`Published "${result.topic}" to Encye`);
    } catch (error) {
      notify(error.response?.data?.detail || error.message);
    } finally {
      setPublishingTopics(prev => {
        const next = new Set(prev);
        next.delete(result.topic);
        return next;
      });
      finishOperation?.();
    }
  };

  const publishAllResults = async () => {
    const pending = results.filter(result => result.summary && !result.encyeLexiconId);
    if (!pending.length || bulkPublishing) return;
    let libraryDecision;
    let duplicateDecision;
    try {
      libraryDecision = await confirmLibraryDuplicateAction(pending, {
        canManageLexicons,
        getTitle: result => result.topic,
        removeItems: rows => {
          const topicsToRemove = new Set(rows.map(row => normalizeQueueTitle(row.topic)));
          setResults(prev => prev.filter(row => !topicsToRemove.has(normalizeQueueTitle(row.topic))));
        }
      });
      if (!libraryDecision.proceed) return;
      duplicateDecision = await confirmEncyeDuplicatePublish(pending.map(result => result.topic), { canManageLexicons });
    } catch (error) {
      notify(error.response?.data?.detail || error.message);
      return;
    }
    if (!duplicateDecision.proceed) return;
    let publishedCount = 0;
    let failedCount = 0;
    setBulkPublishing(true);
    startOperation?.({
      title: 'Publishing batch to Encye',
      detail: 'Saving generated lexicons, uploading them to Encye, and confirming each record.',
      duration: Math.max(16000, pending.length * 5000),
      steps: ['Prepare batch', 'Save lexicons', 'Upload to Encye', 'Finalize']
    });
    setPublishingTopics(prev => new Set([...prev, ...pending.map(result => result.topic)]));
    for (const result of pending) {
      try {
        let articleId = result.articleId;
        if (!articleId) {
          const saved = await saveLexiconArticle({
            title: result.topic,
            slug: slugify(result.topic),
            content: result.summary,
            published: true,
            overrideExisting: libraryDecision.overrideExisting
          });
          articleId = saved.data._id;
          setResults(prev => prev.map(row => row.topic === result.topic ? { ...row, articleId } : row));
        }
        const response = await publishLexiconArticleToEncye(articleId, {
          canManageLexicons,
          overrideEncye: duplicateDecision.overrideEncye
        });
        setResults(prev => prev.map(row => row.topic === result.topic ? {
          ...row,
          articleId,
          encyeLexiconId: response.data.article.encyeLexiconId
        } : row));
        publishedCount += 1;
      } catch (error) {
        failedCount += 1;
      } finally {
        setPublishingTopics(prev => {
          const next = new Set(prev);
          next.delete(result.topic);
          return next;
        });
      }
    }
    setBulkPublishing(false);
    finishOperation?.();
    notify(`Bulk publish complete: ${publishedCount} published${failedCount ? `, ${failedCount} failed` : ''}.`);
  };

  const handleExport = () => {
    const data = results.map(item => ({
      topic: item.topic,
      status: item.summary ? 'done' : 'error',
      summary: item.summary
    }));
    exportToCsv(data, 'lexicon_batch_export.csv');
  };

  return (
    <div className="lexicon-batch-grid">
      <div className="lexicon-column">
        <SummaryOptions
          batch
          options={options}
          values={{
            style: workspaceState.batchStyle,
            length: workspaceState.batchLength,
            useCustomPrompt: workspaceState.batchUseCustomPrompt,
            customPrompt: workspaceState.batchCustomPrompt
          }}
          onChange={(patch) => updateWorkspace({
            batchStyle: patch.style ?? workspaceState.batchStyle,
            batchLength: patch.length ?? workspaceState.batchLength,
            batchUseCustomPrompt: patch.useCustomPrompt ?? workspaceState.batchUseCustomPrompt,
            batchCustomPrompt: patch.customPrompt ?? workspaceState.batchCustomPrompt
          })}
        />
        <div className="lexicon-panel">
          <div className="lexicon-panel-header">
            <h3>Bulk intake</h3>
            <p>Paste many lexicon titles, then validate existing entries and variants before mass generation.</p>
          </div>
          <textarea
            rows="6"
            value={csvText}
            onChange={(event) => setCsvText(event.target.value)}
            placeholder={'Black hole\nPhotosynthesis\nMars, planet'}
          />
          <div className="lexicon-inline-actions">
            <button className="btn btn-primary" onClick={handleAddCsv} disabled={!csvText.trim()}>Add topics</button>
          </div>
        </div>
      </div>

      <div className="lexicon-column">
        <div className="lexicon-panel">
          <div className="lexicon-panel-toolbar">
            <div>
              <h3>Mass creation planner</h3>
              <p>{topics.length === 0 ? 'No topics yet.' : `${topics.length} planned topics, ${variantCount} marked variant, ${existingCount} existing.`}</p>
            </div>
            <div className="lexicon-inline-actions">
              <button className="btn btn-outline" onClick={searchVariants} disabled={!topics.length || running || searchingVariants || pendingVariantCount === 0}>
                <Search size={14} /> {searchingVariants ? 'Searching...' : `Search variants${pendingVariantCount ? ` (${pendingVariantCount})` : ''}`}
              </button>
              <button className="btn btn-primary" onClick={summarizeBatch} disabled={!topics.length || running}>
                {running ? 'Generating...' : 'Generate mass batch'}
              </button>
              <button className="btn btn-outline" onClick={() => setTopics([])} disabled={running || !topics.length}>Clear</button>
            </div>
          </div>
          <div className="lexicon-table-wrap lexicon-mass-table-wrap">
            <table className="lexicon-mass-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Topic</th>
                  <th>Checks</th>
                  <th>Status</th>
                  <th>Summary</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {topics.map((topic, index) => (
                  <tr key={topic.id}>
                    <td>{index + 1}</td>
                    <td>
                      <strong>{topic.title}</strong>
                      {topic.error && <small className="lexicon-error-text">{topic.error}</small>}
                    </td>
                    <td>
                      <div className="lexicon-qcard-tags">
                        {topic.presentInLibrary && <span className="lexicon-status lexicon-status-library">In library</span>}
                        {topic.presentInEncye && <span className="lexicon-status lexicon-status-done">In Encye</span>}
                        {topic.isVariant && <span className="lexicon-status lexicon-status-running">Variant</span>}
                        {topic.variantChecked && !topic.isVariant && <span className="lexicon-status lexicon-status-pending">Not variant</span>}
                        {!topic.variantChecked && <span className="lexicon-status-badge status-pending">Unchecked</span>}
                      </div>
                    </td>
                    <td><span className={`lexicon-status-badge status-${topic.status || 'pending'}`}>{topic.status || 'pending'}</span></td>
                    <td>{topic.summary ? <SummaryPreview text={topic.summary} /> : <span className="lexicon-muted-text">Not generated</span>}</td>
                    <td>
                      <div className="lexicon-inline-actions" style={{ marginTop: 0 }}>
                        {topic.isVariant && (topic.variantCandidates || []).length > 0 && (
                          <button className="btn btn-outline btn-sm" onClick={() => addAllVariants(topic)} disabled={running}>
                            <ListPlus size={12} /> Add variants ({topic.variantCandidates.length})
                          </button>
                        )}
                        <button className="btn btn-outline btn-sm btn-remove" onClick={() => setTopics(prev => prev.filter(item => item.id !== topic.id))} disabled={running}>
                          <Trash size={12} /> Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {topics.length === 0 && (
                  <tr>
                    <td colSpan="6"><div className="lexicon-empty">Bulk topics appear here after you add titles.</div></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {results.length > 0 && (
          <div className="lexicon-panel">
            <div className="lexicon-panel-toolbar">
              <div>
                <h3>Results</h3>
                <p>{results.length} summaries split from one response.</p>
              </div>
              <div className="lexicon-inline-actions">
                <TokenUsageStat usage={usage} itemCount={results.length || topics.length} />
                <button className="btn btn-outline" onClick={handleExport} disabled={results.length === 0}>
                  <Download size={14} /> Export CSV
                </button>
                <button
                  className="btn btn-primary"
                  onClick={publishAllResults}
                  disabled={bulkPublishing || !results.some(result => result.summary && !result.encyeLexiconId)}
                >
                  <Ship size={14} /> {bulkPublishing ? 'Uploading to Encye...' : 'Bulk upload to Encye'}
                </button>
              </div>
            </div>
            <div className="lexicon-list-stack">
              {results.map((result, index) => (
                <div className="lexicon-queue-row" key={`${result.topic}-${index}`}>
                  <div className="lexicon-row-head">
                    <strong>{result.topic}</strong>
                    {result.summary && (
                      <div className="lexicon-inline-actions" style={{ marginTop: 0 }}>
                        <button className="btn btn-outline btn-sm btn-save" onClick={() => saveResult(result)} disabled={Boolean(result.articleId)}>
                          <Download size={12} /> {result.articleId ? 'Saved' : 'Save to Library'}
                        </button>
                        <button className="btn btn-primary btn-sm" onClick={() => publishResult(result)} disabled={publishingTopics.has(result.topic) || Boolean(result.encyeLexiconId)}>
                          <Ship size={12} /> {result.encyeLexiconId ? 'In Encye' : publishingTopics.has(result.topic) ? 'Publishing...' : 'Publish to Encye'}
                        </button>
                      </div>
                    )}
                  </div>
                  {result.summary ? (
                    <SummaryPreview text={result.summary} />
                  ) : (
                    <p className="lexicon-error-text">No summary was returned for this topic.</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Library({ notify, canManageLexicons, startOperation, finishOperation }) {
  const libraryFilters = [
    { value: 'all', label: 'All' },
    { value: 'published_to_encye', label: 'Published to Encye' },
    { value: 'variants', label: 'Having variants' },
    { value: 'not_published_to_encye', label: 'Not published to Encye' },
    { value: 'no_variants', label: 'No variants' }
  ];
  const [articles, setArticles] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [filterOpen, setFilterOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [libraryPageSize, setLibraryPageSize] = useState(10);
  const [totalArticles, setTotalArticles] = useState(0);
  const [libraryHasMore, setLibraryHasMore] = useState(false);
  const [libraryTotalKnown, setLibraryTotalKnown] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [publishingId, setPublishingId] = useState(null);

  useEffect(() => {
    const id = setTimeout(() => {
      setQ(searchInput.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  const loadArticles = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        q,
        filter,
        offset: page * libraryPageSize,
        limit: libraryPageSize
      };
      const response = await lexiconAgentService.listArticles(params);
      const rows = Array.isArray(response.data) ? response.data : (response.data.articles || []);
      setArticles(rows);
      const headerTotal = response.headers?.['x-total-count'];
      const responseTotal = Array.isArray(response.data) ? headerTotal : response.data.total;
      const hasReportedTotal = Number.isFinite(Number(responseTotal));
      setLibraryTotalKnown(hasReportedTotal);
      setTotalArticles(hasReportedTotal ? Number(responseTotal) : 0);
      setLibraryHasMore(hasReportedTotal
        ? (page + 1) * libraryPageSize < Number(responseTotal)
        : rows.length === libraryPageSize);
      if (!hasReportedTotal) {
        console.warn('[lexicon library] API did not return total. Expected { articles, total } or X-Total-Count header.', response.data);
      }
    } catch (error) {
      notify(error.message);
    } finally {
      setLoading(false);
    }
  }, [filter, libraryPageSize, notify, page, q]);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  const updateArticle = async (article, patch) => {
    try {
      const response = await lexiconAgentService.updateArticle(article._id, patch);
      setArticles(prev => prev.map(row => row._id === article._id ? response.data : row));
      if (selected?._id === article._id) setSelected(response.data);
      notify('Changes saved');
    } catch (error) {
      notify(error.response?.data?.detail || error.message);
    }
  };

  const deleteArticle = async (article) => {
    try {
      await lexiconAgentService.deleteArticle(article._id);
      setArticles(prev => prev.filter(row => row._id !== article._id));
      if (selected?._id === article._id) setSelected(null);
      notify('Deleted');
    } catch (error) {
      notify(error.response?.data?.detail || error.message);
    }
  };

  const publishArticle = async (article) => {
    if (article.encyeLexiconId || publishingId === article._id) {
      if (article.encyeLexiconId) notify('This lexicon is already in Encye.');
      return;
    }
    let duplicateDecision;
    try {
      duplicateDecision = await confirmEncyeDuplicatePublish([article.title], { canManageLexicons });
    } catch (error) {
      notify(error.response?.data?.detail || error.message);
      return;
    }
    if (!duplicateDecision.proceed) return;
    try {
      setPublishingId(article._id);
      startOperation?.({
        title: 'Publishing lexicon to Encye',
        detail: 'Sending the lexicon to Encye, linking related variants, and confirming publication.',
        duration: 14000,
        steps: ['Validate lexicon', 'Upload to Encye', 'Link variants', 'Confirm publish']
      });
      const response = await publishLexiconArticleToEncye(article._id, {
        canManageLexicons,
        overrideEncye: duplicateDecision.overrideEncye
      });
      const updated = response.data.article;
      setArticles(prev => prev.map(row => row._id === updated._id ? updated : row));
      if (selected?._id === updated._id) setSelected(updated);
      notify(`Published "${updated.title}" to Encye`);
    } catch (error) {
      notify(error.response?.data?.detail || error.message);
    } finally {
      setPublishingId(null);
      finishOperation?.();
    }
  };

  const openArticle = async (articleRef) => {
    const articleId = articleRef?._id || articleRef;
    if (!articleId) return;
    try {
      const response = await lexiconAgentService.getArticle(articleId);
      const fullArticle = response.data;
      setSelected(fullArticle);
      setArticles(prev => (
        prev.some(row => row._id === fullArticle._id)
          ? prev.map(row => row._id === fullArticle._id ? fullArticle : row)
          : [fullArticle, ...prev]
      ));
    } catch (error) {
      notify(error.response?.data?.detail || error.message);
    }
  };
  const totalLibraryPages = libraryTotalKnown ? Math.max(1, Math.ceil(totalArticles / libraryPageSize)) : null;
  const activeFilter = libraryFilters.find(item => item.value === filter) || libraryFilters[0];

  return (
    <div className="lexicon-library-grid">
      <div className="lexicon-panel">
        <div className="lexicon-panel-header">
          <h3>Organization lexicon library</h3>
          <p>Browse lexicons in the selected organization, manage drafts, variants, and Encye publishing.</p>
        </div>
        <div className="lexicon-library-toolbar">
          <input
            type="search"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search titles"
          />
          <div className="lexicon-filter-menu">
            <button
              type="button"
              className="lexicon-filter-trigger"
              onClick={() => setFilterOpen(value => !value)}
            >
              <Filter size={15} /> {activeFilter.label}
              <ChevronDown size={15} />
            </button>
            {filterOpen && (
              <div className="lexicon-filter-popover">
                {libraryFilters.map(({ value, label }) => (
              <button
                key={value}
                className={filter === value ? 'active' : ''}
                onClick={() => {
                  setFilter(value);
                  setPage(0);
                  setFilterOpen(false);
                }}
              >
                {label}
              </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="lexicon-empty">Loading...</div>
        ) : articles.length === 0 ? (
          <div className="lexicon-empty">No saved entries match your filters.</div>
        ) : (
          <div className="lexicon-list-stack">
            {articles.map(article => (
              <button className="lexicon-library-row" key={article._id} onClick={() => setSelected(article)}>
                <div className="lexicon-library-row-top">
                  <div className="lexicon-library-row-title">
                    <strong>{article.title}</strong>
                    <span className={`lexicon-status ${article.encyeLexiconId ? 'lexicon-status-done' : 'lexicon-status-pending'}`}>
                      {article.encyeLexiconId ? 'Published' : 'Added to Library'}
                    </span>
                    {article.variantIds?.length > 0 && (
                      <span className="lexicon-status lexicon-status-running">
                        {article.variantIds.length} variant{article.variantIds.length === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                  <div className="lexicon-library-row-actions">
                    {article.encyeLexiconId ? (
                      <span className="lexicon-encye-published">
                        <Check size={14} /> Published to Encye
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={(event) => {
                          event.stopPropagation();
                          publishArticle(article);
                        }}
                        disabled={publishingId === article._id}
                      >
                        <Ship size={13} /> {publishingId === article._id ? 'Publishing...' : 'Publish to Encye'}
                      </button>
                    )}
                  </div>
                </div>
                <p>{stripMarkdownSyntax(article.content)}</p>
                <div className="lexicon-library-row-meta">
                  <small>Saved {new Date(article.created_at).toLocaleDateString()}</small>
                  <small>{article.sourceType || 'manual'}</small>
                </div>
              </button>
            ))}
          </div>
        )}
        <div className="lexicon-pagination">
          <button className="btn btn-outline" disabled={page === 0} onClick={() => setPage(prev => Math.max(0, prev - 1))}>Prev</button>
          <div className="lexicon-pagination-center">
          <span>
            {libraryTotalKnown
              ? `Page ${Math.min(page + 1, totalLibraryPages)} of ${totalLibraryPages}`
              : `Page ${page + 1}`}
          </span>
            <label>
              Show
              <select
                value={libraryPageSize}
                onChange={(event) => {
                  setLibraryPageSize(Number(event.target.value));
                  setPage(0);
                }}
              >
                {LIBRARY_PAGE_SIZE_OPTIONS.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </label>
          </div>
          <button className="btn btn-outline" disabled={!libraryHasMore} onClick={() => setPage(prev => prev + 1)}>Next</button>
        </div>
      </div>

      {selected && (
        <ArticleEditor
          article={selected}
          onClose={() => setSelected(null)}
          onSave={updateArticle}
          onDelete={deleteArticle}
          onPublishToEncye={publishArticle}
          onOpenArticle={openArticle}
          publishingToEncye={publishingId === selected._id}
          canManageLexicons={canManageLexicons}
        />
      )}
    </div>
  );
}

function ArticleEditor({ article, onClose, onSave, onDelete, onPublishToEncye, onOpenArticle, publishingToEncye, canManageLexicons }) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [variantPage, setVariantPage] = useState(1);
  const [variantSearch, setVariantSearch] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    setTitle(article?.title || '');
    setContent(article?.content || '');
    setVariantPage(1);
    setVariantSearch('');
    setIsEditing(false);
    modalRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [article]);

  if (!article) return null;

  const dirty = title !== article.title || content !== article.content;
  const variants = Array.isArray(article.variantIds) ? article.variantIds : [];
  const searchQuery = variantSearch.trim().toLowerCase();
  const filteredVariants = variants.filter(variant => {
    if (!searchQuery) return true;
    return [
      variant.title || variant,
      variant.descriptionHint,
      variant.description,
      variant.sourceUrl,
      variant.encyeLexiconId ? 'published' : 'added to library'
    ].some(value => String(value || '').toLowerCase().includes(searchQuery));
  });
  const variantTotalPages = Math.max(1, Math.ceil(filteredVariants.length / VARIANT_PAGE_SIZE));
  const currentVariantPage = Math.min(variantPage, variantTotalPages);
  const variantStartIndex = (currentVariantPage - 1) * VARIANT_PAGE_SIZE;
  const visibleVariants = filteredVariants.slice(variantStartIndex, variantStartIndex + VARIANT_PAGE_SIZE);
  const articleUrl = withSelegicUtm(article.sourceUrl || '');
  const { summary, sourceUrl } = summaryDisplayParts(content, articleUrl);
  const subtitle = summary.match(/\((\d{4}[–-]\d{4})\)/)?.[0]
    || (article.title !== title ? article.title : 'Lexicon article');
  const savedDate = article.created_at ? new Date(article.created_at) : new Date();
  const language = article.language || 'English';
  const sourceLabel = article.sourceType === 'web' ? 'Web' : 'Manual';
  const displaySourceUrl = sourceUrl || articleUrl;

  return (
    <div className="lexicon-modal-backdrop" onClick={onClose}>
      <div className="lexicon-modal lexicon-article-editor" ref={modalRef} onClick={(event) => event.stopPropagation()}>
        <div className="lexicon-article-editor-header">
          <button className="lexicon-editor-back" onClick={onClose}>
            <ArrowLeft size={18} /> Back
          </button>
          <div className="lexicon-editor-header-actions">
            <button className="lexicon-editor-icon-btn" type="button">
              <Share2 size={17} /> Share
            </button>
            {canManageLexicons && (
              <button className="lexicon-editor-icon-btn" type="button" onClick={() => setIsEditing(value => !value)}>
                <Edit3 size={17} /> {isEditing ? 'Preview' : 'Edit'}
              </button>
            )}
            <span className="lexicon-editor-divider" />
            <button className="lexicon-editor-close" onClick={onClose} aria-label="Close">
              <X size={20} />
            </button>
          </div>
        </div>

        <section className="lexicon-editor-hero">
          <div>
            {isEditing ? (
              <input className="lexicon-editor-title-input" value={title} onChange={(event) => setTitle(event.target.value)} />
            ) : (
              <h2>{title}</h2>
            )}
            <p>{subtitle}</p>
            <div className="lexicon-editor-source-row">
              <span className="lexicon-editor-source-icon"><Globe2 size={14} /></span>
              {displaySourceUrl ? (
                <a href={displaySourceUrl} target="_blank" rel="noreferrer">{sourceLabel}</a>
              ) : (
                <span>{sourceLabel}</span>
              )}
              <span className="lexicon-editor-status-chip">
                <span /> {article.encyeLexiconId ? 'Published' : 'Added to Library'}
              </span>
            </div>
          </div>
          <span className="lexicon-editor-saved-chip">
            <Check size={16} /> Saved
          </span>
        </section>

        <section className="lexicon-editor-summary-card">
          <div className="lexicon-editor-summary-icon"><FileText size={18} /></div>
          <div className="lexicon-editor-summary-content">
            <h3>Summary</h3>
            {isEditing ? (
              <textarea rows="8" value={content} onChange={(event) => setContent(event.target.value)} />
            ) : (
              <p>{summary || 'No summary is available for this article.'}</p>
            )}
          </div>
          <div className="lexicon-editor-watermark" aria-hidden="true">
            <Hexagon size={58} strokeWidth={1.7} />
          </div>
        </section>

        <div className="lexicon-editor-meta-row">
          <div className="lexicon-editor-meta-item">
            <span><CalendarDays size={17} /></span>
            <div><small>Saved</small><strong>{savedDate.toLocaleString()}</strong></div>
          </div>
          <div className="lexicon-editor-meta-item">
            <span><Globe2 size={17} /></span>
            <div><small>Source</small><strong>{sourceLabel}</strong></div>
          </div>
          <div className="lexicon-editor-meta-item">
            <span><Languages size={17} /></span>
            <div><small>Language</small><strong>{language}</strong></div>
          </div>
        </div>

        {article.encyePublicationError && !article.encyeLexiconId && (
          <small className="lexicon-error-text">Last Encye attempt: {article.encyePublicationError}</small>
        )}
        {variants.length > 0 && (
          <div className="lexicon-variant-list">
            <div className="lexicon-variant-list-header">
              <div>
                <strong>Related Variants ({variants.length})</strong>
                <small>{variants.length} related {variants.length === 1 ? 'entry' : 'entries'}</small>
              </div>
              <label className="lexicon-variant-search">
                <Search size={16} />
                <input
                  value={variantSearch}
                  onChange={(event) => {
                    setVariantSearch(event.target.value);
                    setVariantPage(1);
                  }}
                  placeholder="Search variants..."
                  aria-label="Search variants"
                />
              </label>
            </div>
            <div className="lexicon-variant-grid">
              {visibleVariants.length === 0 && (
                <div className="lexicon-variant-empty">
                  No variants match your search.
                </div>
              )}
              {visibleVariants.map(variant => {
                const variantTitle = variant.title || variant;
                const variantImage = variant.imageUrl || variant.thumbnailUrl || variant.photoUrl;
                return (
                  <button
                    type="button"
                    className="lexicon-variant-row"
                    key={variant._id || variantTitle}
                    onClick={() => onOpenArticle?.(variant)}
                    title={`Open ${variantTitle}`}
                  >
                    <div className="lexicon-variant-media" aria-hidden="true">
                      {variantImage ? (
                        <img src={variantImage} alt="" />
                      ) : (
                        <FileText size={18} />
                      )}
                    </div>
                    <div className="lexicon-variant-main">
                      <span>{variantTitle}</span>
                      <p>{variant.descriptionHint || variant.description || 'Related lexicon entry variant.'}</p>
                      {variant.sourceUrl && (
                        <a href={withSelegicUtm(variant.sourceUrl)} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}>Source</a>
                      )}
                    </div>
                    <small>{variant.encyeLexiconId ? 'Published' : 'Added to Library'}</small>
                  </button>
                );
              })}
            </div>
            <div className="lexicon-variant-pagination">
              <div className="lexicon-variant-page-controls">
                <button
                  type="button"
                  className="page-btn"
                  onClick={() => setVariantPage(page => Math.max(1, page - 1))}
                  disabled={currentVariantPage === 1}
                  aria-label="Previous variants page"
                >
                  <ChevronLeft size={15} />
                </button>
                <span>Page {currentVariantPage} of {variantTotalPages}</span>
                <button
                  type="button"
                  className="page-btn"
                  onClick={() => setVariantPage(page => Math.min(variantTotalPages, page + 1))}
                  disabled={currentVariantPage === variantTotalPages}
                  aria-label="Next variants page"
                >
                  <ChevronRight size={15} />
                </button>
              </div>
              <small>
                {filteredVariants.length
                  ? `${variantStartIndex + 1}-${Math.min(variantStartIndex + visibleVariants.length, filteredVariants.length)} of ${filteredVariants.length}`
                  : '0 of 0'}
              </small>
            </div>
          </div>
        )}
        <div className="lexicon-form-actions">
          {canManageLexicons && (
            <>
              <button className="btn btn-danger" onClick={() => onDelete(article)}>Delete</button>
            </>
          )}
          <button
            className="btn btn-secondary"
            onClick={() => onPublishToEncye(article)}
            disabled={publishingToEncye || Boolean(article.encyeLexiconId) || dirty}
            title={dirty ? 'Save changes before publishing to Encye' : undefined}
          >
            <Ship size={14} /> {article.encyeLexiconId ? 'Already in Encye' : publishingToEncye ? 'Publishing...' : 'Publish to Encye'}
          </button>
          {canManageLexicons && (
            <button
              className="btn btn-primary"
              onClick={() => {
                if (dirty) onSave(article, { title, content });
                else onClose();
              }}
            >
              <Check size={15} /> {dirty ? 'Save changes' : 'Done'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Settings({ options, styles, refreshAgent, notify }) {
  const [defaultStyle, setDefaultStyle] = useState('');
  const [defaultLength, setDefaultLength] = useState(200);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    setDefaultStyle(options.default_style || '');
    setDefaultLength(options.default_length || 200);
  }, [options]);

  const saveDefaults = async () => {
    try {
      await lexiconAgentService.updateSettings({
        default_style: defaultStyle,
        default_length: defaultLength
      });
      await refreshAgent();
      notify('Defaults saved');
    } catch (error) {
      notify(error.response?.data?.detail || error.message);
    }
  };

  const deleteStyle = async (value) => {
    try {
      await lexiconAgentService.deleteStyle(value);
      await refreshAgent();
      notify('Style deleted');
    } catch (error) {
      notify(error.response?.data?.detail || error.message);
    }
  };

  return (
    <div className="lexicon-settings-grid">
      <div className="lexicon-panel">
        <div className="lexicon-panel-header">
          <h3>Defaults</h3>
          <p>Used when a summary is generated without overriding style or length.</p>
        </div>
        <div className="lexicon-option-grid">
          <label>
            Default style
            <select value={defaultStyle} onChange={(event) => setDefaultStyle(event.target.value)}>
              {options.styles.map(style => (
                <option key={style.value} value={style.value}>{style.label}</option>
              ))}
            </select>
          </label>
          <label>
            Default length
            <input
              type="number"
              min="20"
              max="500"
              step="10"
              value={defaultLength}
              onChange={(event) => setDefaultLength(Number(event.target.value))}
            />
          </label>
        </div>
        <div className="lexicon-form-actions">
          <button className="btn btn-primary" onClick={saveDefaults}>Save defaults</button>
        </div>
      </div>

      <div className="lexicon-panel">
        <div className="lexicon-panel-toolbar">
          <div>
            <h3>Style presets</h3>
            <p>Edit agent guidance or create a new style.</p>
          </div>
          <button className="btn btn-primary" onClick={() => setEditing({ value: '', label: '', guidance: '', isNew: true })}>New style</button>
        </div>
        <div className="lexicon-list-stack">
          {styles.map(style => (
            <div className="lexicon-style-row" key={style.value}>
              <div>
                <strong>{style.label}</strong>
                <span>{style.value}</span>
                <p>{style.guidance}</p>
              </div>
              <div className="lexicon-inline-actions">
                <button className="btn btn-outline" onClick={() => setEditing({ ...style, isNew: false })}>Edit</button>
                <button className="btn btn-danger" onClick={() => deleteStyle(style.value)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {editing && (
        <StyleEditor
          preset={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await refreshAgent();
            notify(editing.isNew ? 'Style created' : 'Style updated');
          }}
          notify={notify}
        />
      )}
    </div>
  );
}

function StyleEditor({ preset, onClose, onSaved, notify }) {
  const [value, setValue] = useState(preset.value);
  const [label, setLabel] = useState(preset.label);
  const [guidance, setGuidance] = useState(preset.guidance);

  const save = async () => {
    try {
      if (preset.isNew) {
        await lexiconAgentService.createStyle({ value, label, guidance });
      } else {
        await lexiconAgentService.updateStyle(preset.value, { label, guidance });
      }
      onSaved();
    } catch (error) {
      notify(error.response?.data?.detail || error.message);
    }
  };

  return (
    <div className="lexicon-modal-backdrop">
      <div className="lexicon-modal">
        <div className="lexicon-panel-header">
          <h3>{preset.isNew ? 'New style' : 'Edit style'}</h3>
          <p>{preset.isNew ? 'Define a new summary style the agent can use.' : 'Update the label and guidance for this style.'}</p>
        </div>
        {preset.isNew && (
          <label>
            ID
            <input
              value={value}
              onChange={(event) => setValue(event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              placeholder="e.g. tweet"
            />
          </label>
        )}
        <label>
          Label
          <input value={label} onChange={(event) => setLabel(event.target.value)} />
        </label>
        <label>
          Guidance
          <textarea rows="6" value={guidance} onChange={(event) => setGuidance(event.target.value)} />
        </label>
        <div className="lexicon-form-actions">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={!label.trim() || !guidance.trim() || (preset.isNew && !value.trim())}>Save</button>
        </div>
      </div>
    </div>
  );
}

export default LexiconWorkspace;
